import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { intervalLabel, intervalDueDaysBetween } from "../../utils/habitInterval";
import { Calendar, ChevronLeft, ChevronRight, ChevronUp, Pencil, X } from "lucide-react";
import { LockBadge } from "../../components/LockBadge";
import Modal from "../../components/Modal";
import { AddAmountSheet } from "../../components/AddAmountSheet";
import { isAmountHabit, goalTag, formatProgress, loggedAmount } from "../../utils/habitUnits";
import { Card } from "../../components/Card";
import { CategoryIcon } from "../../components/CategoryIcon";
import { HabitIcon } from "../../components/HabitIcon";
import { BarChart } from "../../components/BarChart";
import { FocusPieChart } from "../../components/charts/FocusCharts";
import { MONTHS, DAYS_SHORT } from "../../constants/dates";
import { PRIORITY_COLORS } from "../../constants/palettes";
import { load, save } from "../../utils/storage";
import { now, fmt, todayStr, localDateStr, daysInMonth } from "../../utils/dateHelpers";
import { themeTone, vividPastel, ACTIVE_BG_THEME } from "../../theme/colorEngine";
import { clockInInk } from "../../theme/clockInInk";
import { S } from "../../theme/sharedStyles";

import {
  isHabitDueOn, isHabitDoneOn, limitedOffDays, isLimitedRestrictedOn, getPeriodStart, getPeriodBounds,
  getConsistencyScore, getMonthlyBreakdown, allowancePeriodBounds, allowanceUsed,
  restrictedDayColor, HABIT_PERIOD_DAYS, isHabitExcludedOn,
} from "../../utils/habitLogic";
import { usePremium } from "../../firebase/billing";
import { habitPeriodRange as sharedHabitPeriodRange, todoWeeklyTarget, isTodoCompleteOn, goalOn, minusExcluded } from "../../utils/habitWeeklyGoal";
import ScrollCoachMark from "../../onboarding/ScrollCoachMark";
import { hasTickedHabit, isTargetCompletionGuideDone, markTargetCompletionGuideDone, isOverallCompletionGuideDone, markOverallCompletionGuideDone } from "../../onboarding/onboardingStorage";
import { vibrantAccent } from "../../capacitor/widgetColor";

// One colour for a day that hasn't come yet, in every tracker view (Weekly, 4-Week,
// Yearly; Jade, 2026-10-19). A translucent grey rather than a solid colour + opacity,
// so it shows on any card; dark mode swaps it via .trk-future in darkModeCss.
const FUTURE_BLOCK_BG = "rgba(221, 216, 228, 0.5)";


// Shown in place of the Weekly/Monthly/Yearly Goal tracker and the Overall
// Completion chart when the viewer isn't Premium. `description` is passed
// per call site since the two features do different things and shouldn't
// share one generic blurb. Not tappable — just a locked indicator; the
// Settings panel's own Upgrade button is the one path to the Premium page.
// Habits History buttons act on touchend (so the tap is instant), but a swipe
// that starts on one also ends with a touchend: scrolling the list past the
// arrow buttons opened "Mark done" (Jade, 2026-10-26). Only a touch that stayed
// put counts as a tap.
const TAP_SLOP = 10;
function tapOnly(fn) {
  return {
    onTouchStart: e => { const t = e.touches[0]; e.currentTarget._tapStart = t ? { x: t.clientX, y: t.clientY } : null; },
    onTouchEnd: e => {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      const start = e.currentTarget._tapStart;
      e.currentTarget._tapStart = null;
      const t = e.changedTouches[0];
      if (!start || !t || Math.abs(t.clientX - start.x) > TAP_SLOP || Math.abs(t.clientY - start.y) > TAP_SLOP) return;
      fn();
    },
  };
}

function PremiumLockedCard({ description }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 8, padding: "28px 16px", textAlign: "center",
    }}>
      <LockBadge size={36} />
      <p style={{ fontSize: 12, color: "var(--c-text-soft)", margin: 0, maxWidth: 260 }}>
        {description}
      </p>
    </div>
  );
}

function ProfileTabInner({ habits = [], setHabits = () => {}, habitLogs = {}, setHabitLogs, habitSkips = [], setHabitSkips = () => {}, tasks = [], setTasks, focusSessions = [], setFocusSessions = () => {}, diary = {}, categories = [], setCategories = null, showBackToTop = false, section = "habits", isDesktop = false, statsToolbarSlotNode = null, statsViewSlotNode = null, weekStart, bare = false, wide = false }) {
  // bgTheme isn't read here: it is a prop only so this memoised page redraws
  // when the background changes. Colours like the Overall Completion bars come
  // from themeTone() at render time, and stayed on the old background until
  // something else redrew the page (Jade, 2026-10-23).
  // Category position for grouping habits (Habit Stats lists): the Categories
  // order, with uncategorized (or a deleted category) after all of them.
  const catPos = new Map(categories.map((c, i) => [c.id, i]));
  const catRank = (h) => (h.goalId && catPos.has(h.goalId) ? catPos.get(h.goalId) : categories.length);
  const [displayName, setDisplayName] = useState(() => load("clarity_displayName", ""));
  const [bio, setBio] = useState(() => load("clarity_userBio", ""));
  const [editingName, setEditingName] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [nameTemp, setNameTemp] = useState("");
  const [bioTemp, setBioTemp] = useState("");

  // Target-completion tracker + Overall Completion chart are Premium.
  const { isPremium } = usePremium();

  // Stats state
  const [habitView, setHabitView] = useState("weekly");
  const [habitMonthNav, setHabitMonthNav] = useState({ month: now().getMonth(), year: now().getFullYear() });
  const [habitMonthOffset, setHabitMonthOffset] = useState(() => {
    const starts = habits.map(h => h.habitStartDate || localDateStr(h.createdAt)).filter(Boolean).sort();
    const earliest = starts[0] || fmt(now());
    const originMs = new Date(earliest + "T00:00:00").getTime();
    const todayMs = new Date(fmt(now()) + "T00:00:00").getTime();
    return Math.max(0, Math.floor((todayMs - originMs) / (HABIT_PERIOD_DAYS * 86400000)));
  });
  const [habitWeekOffset, setHabitWeekOffset] = useState(() => {
    const starts = habits.map(h => h.habitStartDate || localDateStr(h.createdAt)).filter(Boolean).sort();
    const earliest = starts[0] || fmt(now());
    const originMs = new Date(earliest + "T00:00:00").getTime();
    const todayMs = new Date(fmt(now()) + "T00:00:00").getTime();
    return Math.max(0, Math.floor((todayMs - originMs) / (7 * 86400000)));
  });
  const [targetWeekOffset, setTargetWeekOffset] = useState(0); // weekly nav of the tracker
  const [phMonthOffset, setPhMonthOffset] = useState(0); // 4-week nav of the tracker (0 = current habit-period window)
  const [phYearOffset, setPhYearOffset] = useState(0);   // yearly nav of the tracker (0 = current 365-day window)
  // Target Completion has its own view and period navigation, separate from
  // the tracker's (Jade, 2026-10-23: picking a view in one changed both).
  const [targetView, setTargetView] = useState("weekly");
  const [tcWeekOffset, setTcWeekOffset] = useState(0);
  const [tcMonthOffset, setTcMonthOffset] = useState(0);
  const [tcYearOffset, setTcYearOffset] = useState(0);
  const [trackerMonthIdx, setTrackerMonthIdx] = useState(now().getMonth());
  const [trackerView, setTrackerView] = useState("weekly");
  const [heatmapTooltip2, setHeatmapTooltip2] = useState(null);
  const [historyView, setHistoryView] = useState("recents");
  const [heatmapTooltip, setHeatmapTooltip] = useState(null); // { label, checked, color, x, y }
  const [navYear, setNavYear] = useState(now().getFullYear());
  const [navMonth, setNavMonth] = useState(now().getMonth());
  const [showAllRecents, setShowAllRecents] = useState(false);
  const [navSelectedDay, setNavSelectedDay] = useState(null);
  const [taskHistoryView, setTaskHistoryView] = useState("completed");
  const [historyDropdown, setHistoryDropdown] = useState("tasks");
  const [historyRecentsDate, setHistoryRecentsDate] = useState(todayStr());
  const [historyEditedItems, setHistoryEditedItems] = useState(() => load("clarity_historyEditedItems", {}));
  useEffect(() => { save("clarity_historyEditedItems", historyEditedItems); }, [historyEditedItems]);
  const [expandedHistoryItem, setExpandedHistoryItem] = useState(null); // "dateStr:id"
  const [habitDetailModal, setHabitDetailModal] = useState(null); // habit object
  const [habitCalendarModal, setHabitCalendarModal] = useState(null); // { habit, month, year }

  // Note: habitWeekOffset / habitMonthOffset are seeded to the current
  // week/month via their useState lazy initializers above. (A previous
  // useEffect here referenced `earliestHabitStart` before its declaration,
  // which threw a temporal-dead-zone error in the minified production build.)

  // Is a restricted habit "clean" on a day? Budget habits are clean unless an
  // attempt was logged that day (same rule as the Target Completion count);
  // the others unless slipped or skipped.
  const isRestrictedDoneOn = (h, dateStr) => h.allowanceMode
    ? !(h.attemptLog || []).includes(dateStr) && !habitSkips.some(s => s.habitId === h.id && s.skippedDate === dateStr)
    : isHabitDoneOn(h, habitLogs, dateStr, habitSkips);
  const [historyAmountSheet, setHistoryAmountSheet] = useState(null); // { habitId, date }
  const toggleHabitLog = (habitId, dateStr) => {
    const habit = habits.find(h => h.id === habitId);
    const isRestricted = habit && (habit.habitType === "nottodo" || habit.habitType === "limited");
    if (isRestricted) {
      // Restricted habits are "done" when there's no slip/skip on that day —
      // they aren't tracked in habitLogs, so toggle the relapse log / skips.
      // Budget ("times per week/month") habits record a slip as an ATTEMPT
      // (attemptLog) — that's what every graph reads for them — so History
      // writes the same thing the "+" button on the Habits tab does.
      const done = isRestrictedDoneOn(habit, dateStr);
      const isBudget = !!habit.allowanceMode;
      if (done) {
        // Mark slipped / used → record it for that day
        setHabits(prev => prev.map(h => {
          if (h.id !== habitId) return h;
          if (isBudget) return (h.attemptLog || []).includes(dateStr) ? h : { ...h, attemptLog: [...(h.attemptLog || []), dateStr] };
          return { ...h, relapseLog: [...(h.relapseLog || []), { date: dateStr, note: "" }] };
        }));
      } else {
        // Mark clean → clear any slip, attempt and skip recorded on that day
        setHabits(prev => prev.map(h => h.id === habitId
          ? { ...h, relapseLog: (h.relapseLog || []).filter(r => r.date !== dateStr), ...(isBudget ? { attemptLog: (h.attemptLog || []).filter(d => d !== dateStr) } : {}) }
          : h));
        setHabitSkips(prev => prev.filter(s => !(s.habitId === habitId && s.skippedDate === dateStr)));
      }
    } else if (isAmountHabit(goalOn(habit, dateStr))) {
      // Unit habits (steps, glasses, …) log an amount, not done/missed: open
      // the same Add amount sheet the Habits tab uses, for that day.
      setHistoryAmountSheet({ habitId, date: dateStr });
    } else {
      // Regular habits: toggle the full daily target (not just 1), so
      // multi-count habits register as actually done.
      const target = goalOn(habit, dateStr)?.dailyCount || 1;
      setHabitLogs(prev => {
        const cur = prev[dateStr]?.[habitId];
        const isDone = typeof cur === "number" ? cur >= target : !!cur;
        return { ...prev, [dateStr]: { ...(prev[dateStr] || {}), [habitId]: isDone ? 0 : target } };
      });
    }
    setHistoryEditedItems(prev => ({ ...prev, [`${dateStr}:${habitId}`]: true }));
    setExpandedHistoryItem(null);
  };
  const toggleTaskDone = (taskId, dateStr) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, done: !t.done, completedAt: !t.done ? new Date().toISOString() : null } : t));
    setHistoryEditedItems(prev => ({ ...prev, [`${dateStr}:${taskId}`]: true }));
    setExpandedHistoryItem(null);
  };
  const trackedScrollRef = React.useRef(null);
  const monthlyScrollRef = React.useRef(null);
  const touchStartX = React.useRef(0);
  const yearlyScrollRefs = React.useRef({});
  const historyCardRef = React.useRef(null);
  const perHabitRef = React.useRef(null);
  // Wraps the actual rendered Weekly/4-Week/Yearly Goal content (not the thin
  // spacer above), so the scroll coach mark below has a real card to
  // spotlight instead of a 20px sliver.
  const completionTargetCardRef = React.useRef(null);
  const barGraphRef = React.useRef(null);
  // Scroll-triggered "first time you reach this section" guide panels — see
  // ScrollCoachMark.jsx. Read once at mount; each flips true (and stays true)
  // the moment its panel is dismissed.
  const [targetGuideDone, setTargetGuideDone] = useState(() => isTargetCompletionGuideDone());
  const [overallGuideDone, setOverallGuideDone] = useState(() => isOverallCompletionGuideDone());
  const [overallBarSel, setOverallBarSel] = React.useState(null); // selected month index in Overall Completion
  const [overallYearOffset, setOverallYearOffset] = React.useState(0); // 0 = Year 1 (first 12 months from start date)
  const donutChartRef = React.useRef(null);
  useEffect(() => {
    const yr = now().getFullYear();
    const arr = trackerView === "weekly"
      ? (() => { const start = new Date(now()); start.setDate(now().getDate() - now().getDay()); return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return fmt(d); }); })()
      : Array.from({ length: daysInMonth(yr, trackerMonthIdx) }, (_, i) => { const d = new Date(yr, trackerMonthIdx, i + 1); return fmt(d); });
    const todayIdx = arr.indexOf(todayStr());
    if (trackerView === "monthly") {
      // Scroll so today is the first visible column
      if (monthlyScrollRef.current && todayIdx >= 0) {
        const COL_W = 20; // 14px box + gap
        monthlyScrollRef.current.scrollLeft = Math.max(0, todayIdx * COL_W);
      }
    } else if (trackerView === "weekly") {
      // No auto-scroll for weekly — all 7 days should be visible at once
      if (trackedScrollRef.current) trackedScrollRef.current.scrollLeft = 0;
    } else if (trackerView === "yearly") {
      const isLeap = (yr % 4 === 0 && yr % 100 !== 0) || yr % 400 === 0;
      const startOfYear = new Date(yr, 0, 1);
      const dayOfYear = Math.floor((now() - startOfYear) / 86400000);
      const YCOL_W = 18;
      Object.values(yearlyScrollRefs.current).forEach(ref => {
        if (ref) ref.scrollLeft = Math.max(0, dayOfYear * YCOL_W);
      });
    }
  }, [trackerView, trackerMonthIdx]);
  const startDate = localStorage.getItem("clarity_startDate") || todayStr();

  const saveName = () => { setDisplayName(nameTemp); save("clarity_displayName", nameTemp); setEditingName(false); };
  const saveBio = () => { setBio(bioTemp); save("clarity_userBio", bioTemp); setEditingBio(false); };

  // Stats helpers
  const getDateRange = (viewType) => {
    const d = now(); const dates = [];
    if (viewType === "weekly") {
      const start = new Date(d); start.setDate(d.getDate() - 6);
      for (let i = 0; i <= 6; i++) { const dd = new Date(start); dd.setDate(start.getDate() + i); dates.push(fmt(dd)); }
    } else if (viewType === "monthly") {
      const days = daysInMonth(d.getFullYear(), d.getMonth());
      for (let i = 1; i <= days; i++) dates.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(i).padStart(2,"0")}`);
    } else {
      for (let m = 0; m < 12; m++) dates.push(`${d.getFullYear()}-${String(m+1).padStart(2,"0")}`);
    }
    // Use earliestHabitStart as cutoff so bars show from when first habit was created
    const cutoff = (earliestHabitStart || startDate).slice(0, viewType === "yearly" ? 7 : 10);
    return dates.filter(dt => dt >= cutoff);
  };

  const getDayRate = useMemo(() => (dateStr) => {
    // Hard cutoff: never show bars before the earliest existing habit was created
    if (dateStr < earliestHabitStart) return null;

    const dateObj = new Date(dateStr + "T00:00:00");
    const dow = dateObj.getDay();
    const monBasedDow = [6,0,1,2,3,4,5][dow]; // Mon=0...Sun=6
    const dayNum = dateObj.getDate();

    // ── Todo habits due on this date ──
    const todoDue = habits.filter(h => {
      if (h.habitType === "nottodo" || h.habitType === "limited") return false;
      if (!h.createdAt || dateStr < localDateStr(h.createdAt)) return false;
      if (h.habitStartDate && dateStr < h.habitStartDate) return false;
      if (h.pausedUntil && dateStr <= h.pausedUntil) return false; // paused on this date — exclude
      if (isHabitExcludedOn(h, dateStr)) return false; // a day off — not due
      if (h.frequencyType === "daily") return true;
      if (h.frequencyType === "interval") return isHabitDueOn(h, dateStr);
      if (h.frequencyType === "specific" && h.dayLabels === "named") return goalOn(h, dateStr).days?.includes(monBasedDow);
      if (h.frequencyType === "specific") return true;
      if (h.frequencyType === "monthly") return goalOn(h, dateStr).monthDates?.includes(dayNum);
      if (h.frequencyType === "count") return !!habitLogs[dateStr]?.[h.id]; // count habits: only show on days they were actually logged
      return false;
    });
    const todoDone = todoDue.filter(h => {
      const v = habitLogs[dateStr]?.[h.id];
      const target = goalOn(h, dateStr).dailyCount || 1;
      return typeof v === "number" ? v >= target : !!v;
    }).length;

    // ── Restricted habits due on this date ──
    const restrictedDue = habits.filter(h => {
      if (!h.createdAt || dateStr < localDateStr(h.createdAt)) return false;
      if (h.habitStartDate && dateStr < h.habitStartDate) return false;
      if (h.pausedUntil && dateStr <= h.pausedUntil) return false; // paused on this date — exclude
      if (isHabitExcludedOn(h, dateStr)) return false; // a day off — not due
      if (h.habitType === "nottodo") return true; // full stop = every day
      if (h.habitType === "limited") {
        return isLimitedRestrictedOn(h, dateStr);
      }
      return false;
    });

    // Score each restricted habit:
    // - slip (relapseLog) = 0 (full deduction)
    // - skip without reschedule = 0 (full deduction)
    // - skip with reschedule = 0.5 (half deduction)
    // - default (no action) = 1 (clean)
    let restrictedScore = 0;
    for (const h of restrictedDue) {
      const slipped = (h.relapseLog || []).some(r => r.date === dateStr);
      const skip = habitSkips?.find(s => s.habitId === h.id && s.skippedDate === dateStr);
      if (slipped) restrictedScore += 0;
      else if (skip && !skip.rescheduledTo) restrictedScore += 0;
      else if (skip && skip.rescheduledTo) restrictedScore += 0.5;
      else restrictedScore += 1; // clean!
    }

    const totalDue = todoDue.length + restrictedDue.length;
    if (totalDue === 0) return null; // nothing due — skip this day in chart

    const totalDone = todoDone + restrictedScore;
    return (totalDone / totalDue) * 100;
  }, [habits, habitLogs, habitSkips]);

  // Default bar-graph nav to the latest start-anchored window (so today is visible)
  const earliestStartForNav = habits.map(h => h.habitStartDate || localDateStr(h.createdAt)).filter(Boolean).sort()[0] || fmt(now());
  useEffect(() => {
    const oMs = new Date(earliestStartForNav + "T00:00:00").getTime();
    const tMs = new Date(fmt(now()) + "T00:00:00").getTime();
    setHabitWeekOffset(Math.max(0, Math.floor((tMs - oMs) / (7 * 86400000))));
    setHabitMonthOffset(Math.max(0, Math.floor((tMs - oMs) / (HABIT_PERIOD_DAYS * 86400000))));
  }, [earliestStartForNav]);

  // Get earliest habit start date
  const earliestHabitStart = useMemo(() => {
    const starts = habits.map(h => h.habitStartDate || localDateStr(h.createdAt)).filter(Boolean).sort();
    return starts[0] || fmt(now());
  }, [habits]);

  // Monthly: current selected calendar month (day 1 to last day)
  const habitMonthDataMemo = useMemo(() => {
    // One habit-period window anchored to the habit start date (D1 = start),
    // like the habit tracker. Window N covers days [N*P, N*P+P-1] after the
    // earliest habit start, where P = HABIT_PERIOD_DAYS.
    const today = fmt(now());
    const originMs = new Date(earliestHabitStart + "T00:00:00").getTime();
    return Array.from({ length: HABIT_PERIOD_DAYS }, (_, i) => {
      const d = new Date(originMs + (habitMonthOffset * HABIT_PERIOD_DAYS + i) * 86400000);
      const dt = fmt(d);
      const label = `${d.getDate()}`;
      if (dt > today) return { label, value: null }; // future — empty slot
      const rate = getDayRate(dt);
      return { label, value: rate !== null ? Math.round(rate) : null };
    });
  }, [habits, habitLogs, habitMonthOffset, earliestHabitStart]);

  // Overall Completion: P1-P12 = 12 consecutive HABIT_PERIOD_DAYS chunks measured
  // from the earliest habit start date (NOT calendar months). M1 = days 0-29
  // from start, M2 = days 30-59, etc. Because each bar spans a whole ~month, we
  // can score COUNTED weekly/monthly habits here too (target vs actual over the
  // window) — unlike the per-day week/month views. Yearly-counted habits have no
  // in-month due, so they're excluded.
  // Takes a year offset (0 = Year 1, the 12 months starting at the earliest
  // habit start date; 1 = Year 2, the next 12; etc.) so Overall Completion can
  // page between years. Not calendar-based — years are 360-day (12×30)
  // stretches measured from the user's own start date.
  const computeHabitYearData = (yearOffset) => {
    if (habits.length === 0) return Array.from({ length: 12 }, (_, mi) => ({ label: `P${mi + 1}`, value: null, done: 0, dueTotal: 0, clean: 0, restrictedTotal: 0, rangeStart: null, rangeEnd: null }));
    const DAY = 86400000;
    const originMs = new Date(earliestHabitStart + "T00:00:00").getTime();
    const todayMs = new Date(fmt(now()) + "T00:00:00").getTime();
    const isRestricted = (h) => h.habitType === "nottodo" || h.habitType === "limited";
    const isActiveDay = (h, dt) => {
      if (h.pausedUntil && dt <= h.pausedUntil) return false;
      if (h.endDate && dt > h.endDate) return false;
      if (isHabitExcludedOn(h, dt)) return false; // a day off — out of both totals
      return true;
    };

    return Array.from({ length: 12 }, (_, mi) => {
      const absMi = yearOffset * 12 + mi;
      const mStartMs = originMs + absMi * HABIT_PERIOD_DAYS * DAY;
      const mEndMs = mStartMs + (HABIT_PERIOD_DAYS - 1) * DAY; // full window (may extend into the future)
      const mLabel = `P${mi + 1}`;
      const rangeStart = fmt(new Date(mStartMs));
      const rangeEnd = fmt(new Date(mEndMs));
      if (mStartMs > todayMs) return { label: mLabel, value: null, done: 0, dueTotal: 0, clean: 0, restrictedTotal: 0, rangeStart, rangeEnd };

      // Accuracy: denominators span the WHOLE month window, not just elapsed
      // days — so completing one day early in the month nudges the bar a little,
      // and it grows steadily toward 100% as the month fills in. Numerators only
      // count up to today. Restricted habits auto-complete: a restricted day
      // with no slip = a clean success.
      let done = 0, dueTotal = 0, clean = 0, restrictedTotal = 0;

      habits.forEach(h => {
        if (h.frequencyType === "yearly") return; // no in-month due
        const hStartStr = h.habitStartDate || localDateStr(h.createdAt);
        if (!hStartStr) return;
        const hStartMs = new Date(hStartStr + "T00:00:00").getTime();
        const wStart = Math.max(mStartMs, hStartMs);
        if (wStart > mEndMs) return; // habit didn't exist during this window

        if (isRestricted(h)) {
          for (let ms = wStart; ms <= mEndMs; ms += DAY) {
            const dt = fmt(new Date(ms));
            if (!isActiveDay(h, dt)) continue;
            const monDow = [6,0,1,2,3,4,5][new Date(dt + "T00:00:00").getDay()];
            let restrictedDay = true;
            if (h.habitType === "limited") restrictedDay = isLimitedRestrictedOn(h, dt);
            if (!restrictedDay) continue;
            restrictedTotal++;
            if (ms <= todayMs && !(h.relapseLog || []).some(r => r.date === dt)) clean++; // auto-clean unless slipped
          }
          return;
        }

        // ── Counted to-do habits: target vs days logged over the full window ──
        if (h.frequencyType === "count" || (h.frequencyType === "monthly" && h.monthlyMode === "count")) {
          let windowDays = 0, logged = 0;
          for (let ms = wStart; ms <= mEndMs; ms += DAY) {
            const dt = fmt(new Date(ms));
            if (!isActiveDay(h, dt)) continue;
            windowDays++;
            if (ms <= todayMs && habitLogs[dt]?.[h.id]) logged++;
          }
          const hw = goalOn(h, fmt(new Date(wStart)));
          const target = h.frequencyType === "count"
            ? Math.max(1, (hw.weeklyCount || 1) * (windowDays / 7))
            : Math.max(1, hw.monthlyCount || 1);
          dueTotal += target;
          done += Math.min(logged, target);
          return;
        }

        // ── Per-day due to-do habits (daily / specific / monthly dates) ──
        for (let ms = wStart; ms <= mEndMs; ms += DAY) {
          const dt = fmt(new Date(ms));
          if (!isActiveDay(h, dt)) continue;
          const dObj = new Date(dt + "T00:00:00");
          const monDow = [6,0,1,2,3,4,5][dObj.getDay()];
          const dayNum = dObj.getDate();
          let due = false;
          if (h.frequencyType === "daily") due = true;
          else if (h.frequencyType === "interval") due = isHabitDueOn(h, dt);
          else if (h.frequencyType === "specific" && h.dayLabels === "named") due = goalOn(h, dt).days?.includes(monDow);
          else if (h.frequencyType === "specific") due = true;
          else if (h.frequencyType === "monthly" && (h.monthlyMode || "dates") === "dates") due = goalOn(h, dt).monthDates?.includes(dayNum);
          if (!due) continue;
          dueTotal++;
          if (ms <= todayMs) { const v = habitLogs[dt]?.[h.id]; if (typeof v === "number" ? v >= (goalOn(h, dt).dailyCount || 1) : !!v) done++; }
        }
      });

      const totalOpp = dueTotal + restrictedTotal;
      // Keep 1 decimal so each individual completion / clean day visibly nudges
      // the score (a whole-percent round hides single ticks once you have a few
      // habits — one tick moves the raw value by only 100/totalOpp points).
      const value = totalOpp > 0 ? Math.round(((done + clean) / totalOpp) * 1000) / 10 : null;
      return { label: mLabel, value, done: Math.round(done), dueTotal: Math.round(dueTotal), clean, restrictedTotal, rangeStart, rangeEnd };
    });
  };

  // computeHabitYearData does a habits.forEach() with an inner day-by-day loop
  // over a 360-day window — recomputing it on every render (as a bare function
  // call in JSX) meant every render of ProfileTab redid this scan regardless of
  // whether habits/logs actually changed. Memoized here so it only reruns when
  // the underlying data or the selected year actually change.
  const overallYearData = useMemo(
    () => computeHabitYearData(overallYearOffset),
    [habits, habitLogs, earliestHabitStart, overallYearOffset]
  );

  const habitWeekDataMemo = useMemo(() => {
    // Anchor week to earliest habit start date (D1 = first box), like the habit tracker.
    // Always render 7 bars: past days show their rate, future days show empty slots.
    const originMs = new Date(earliestHabitStart + "T00:00:00").getTime();
    const todayF = fmt(now());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(originMs + (habitWeekOffset * 7 + i) * 86400000);
      const dt = fmt(d);
      const dayLabel = d.toLocaleDateString("en", { weekday: "short" });
      if (dt > todayF) return { label: dayLabel, value: null }; // future — empty slot
      const rate = getDayRate(dt);
      return { label: dayLabel, value: rate !== null ? Math.round(rate) : null };
    });
  }, [getDayRate, habits, habitLogs, habitSkips, habitWeekOffset, earliestHabitStart]);

  const habitData = (viewType) => viewType === "weekly" ? habitWeekDataMemo : viewType === "yearly" ? computeHabitYearData(0) : habitMonthDataMemo;

  const focusWeekData = useMemo(() => getDateRange("weekly").map(dt => ({
    label: dt.split("-")[2],
    value: focusSessions.filter(s => s.date === dt).reduce((a, s) => a + s.minutes, 0),
  })), [focusSessions]);

  const focusData = (viewType) => focusWeekData;

  const totalFocusWeek = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 6); return focusSessions.filter(s => s.date >= fmt(d)).reduce((a, s) => a + s.minutes, 0); }, [focusSessions]);
  const totalFocusMonth = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 29); return focusSessions.filter(s => s.date >= fmt(d)).reduce((a, s) => a + s.minutes, 0); }, [focusSessions]);
  const totalFocusAll = useMemo(() => focusSessions.reduce((a, s) => a + s.minutes, 0), [focusSessions]);

  const ViewToggle = ({ view, setView }) => (
    <div style={{ display: "flex", gap: 4 }}>
      {["weekly","monthly","yearly"].map(v => (
        <button key={v} onClick={() => setView(v)}
          style={{ ...S.btn, fontSize: 12, padding: "7px 14px", background: view === v ? "var(--c-bg-card)" : "transparent", color: view === v ? "#d4789a" : "#a07090", textTransform: "capitalize", fontWeight: view === v ? 700 : 500, minHeight: 36 }}>{v}</button>
      ))}
    </div>
  );

  const year = now().getFullYear();
  const isLeapYearP = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const totalYearDaysP = isLeapYearP ? 366 : 365;
  const trackerDates = trackerView === "weekly"
    ? (() => { const start = new Date(now()); start.setDate(now().getDate() - now().getDay()); return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return fmt(d); }); })()
    : trackerView === "yearly"
    ? Array.from({ length: totalYearDaysP }, (_, i) => { const d = new Date(year, 0, i + 1); return fmt(d); })
    : Array.from({ length: daysInMonth(year, trackerMonthIdx) }, (_, i) => { const d = new Date(year, trackerMonthIdx, i + 1); return fmt(d); });

  const completedTasks = [...tasks].filter(t => t.done && t.completedAt).sort((a, b) => b.completedAt.localeCompare(a.completedAt));
  const fmtCompletedAt = (iso) => { try { const d = new Date(iso); return `${MONTHS[d.getMonth()].slice(0,3)} ${d.getDate()}, ${d.getFullYear()}`; } catch { return iso?.slice(0,10) || ""; } };

  const TaskPill = ({ task }) => {
    const cat = categories.find(c => c.id === task.goalId);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--c-bg-card)", borderRadius: 12, border: "1.5px solid #f5d8ec" }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: PRIORITY_COLORS[task.priority] || "#ccc", flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--c-text)" }}>{task.title}</span>
        <span style={{ fontSize: 11, color: "var(--c-text-soft)", flexShrink: 0 }}>{fmtCompletedAt(task.completedAt)}</span>
        {cat && <span style={{ fontSize: 10, background: "var(--c-bg-soft)", color: "var(--c-text-soft)", borderRadius: 6, padding: "2px 6px", flexShrink: 0 }}><CategoryIcon cat={cat} size={12} /> {cat.title || cat.name}</span>}
      </div>
    );
  };

  const prevNavMonth = () => { if (navMonth === 0) { setNavMonth(11); setNavYear(y => y-1); } else setNavMonth(m => m-1); setNavSelectedDay(null); };
  const nextNavMonth = () => { if (navMonth === 11) { setNavMonth(0); setNavYear(y => y+1); } else setNavMonth(m => m+1); setNavSelectedDay(null); };
  const navDayStr = (day) => `${navYear}-${String(navMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
  const tasksOnDay = (ds) => completedTasks.filter(t => t.completedAt?.slice(0,10) === ds);
  const hasDiaryEntry = (ds) => { const val = diary?.[ds]; return val && (Array.isArray(val) ? val.length > 0 : true); };

  // The week / 4-week / year navigation sits in the Habit Tracker card's title
  // row, on the right (Jade, 2026-10-23: it read as an odd extra card between
  // the view switch and the habit cards). Each view's nav renders into this slot.
  const [trackerNavSlot, setTrackerNavSlot] = useState(null);
  const TrackerNav = ({ children }) => trackerNavSlot ? createPortal(
    <div className="hs-nav" style={{ display: "flex", alignItems: "center", gap: 2 }}>{children}</div>,
    trackerNavSlot
  ) : null;

  const pencilBtn = (onClick) => (
    <button onClick={onClick} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--c-text-soft)", padding: 4, lineHeight: 1, borderRadius: 6, display: "flex", alignItems: "center" }}><Pencil size={14} /></button>
  );

  // Weekly / 4-Week / Yearly switch. The tracker and Target Completion each
  // have their own (Jade, 2026-10-23); a pick changes only that section.
  const renderViewSwitch = (view, setView, onPick) => (
    <div className="hs-glass hs-seg" style={{ display: "flex", background: "var(--c-bg-soft)", borderRadius: 12, padding: 3, marginBottom: 12, gap: 2 }}>
      {[["weekly","Weekly"],["monthly","4-Week"],["yearly","Yearly"]].map(([v, label]) => (
        <button key={v} onClick={() => { setView(v); onPick(); }}
          style={{
            flex: 1, padding: "7px 0", border: "none", borderRadius: 10, fontSize: 14, fontWeight: view === v ? 700 : 500,
            color: view === v ? "var(--c-text-accent)" : "var(--c-text-soft)",
            background: view === v ? "var(--c-bg-card)" : "transparent",
            boxShadow: view === v ? "0 1px 6px var(--c-shadow)" : "none",
            cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", letterSpacing: 0.2,
          }}>{label}</button>
      ))}
    </div>
  );

  // One Goal table (Weekly / 4-Week / Yearly) inside the Target Completion card.
  const CategorySection = ({ title, description, children, isEmpty }) => (
    <div>
      <div className="hs-glass" style={{ border: "1.5px solid #f0dcea", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 8px rgba(212,120,154,0.07)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: "1.5px solid #f0dcea", background: "var(--c-bg-card)" }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "var(--c-text-accent)", textTransform: "uppercase", letterSpacing: 1 }}>{title}</span>
          {description && <span style={{ fontSize: 11, color: "var(--c-text-soft)", fontWeight: 500 }}>· {description}</span>}
        </div>
        {isEmpty
          ? <p style={{ fontSize: 12, color: "var(--c-text-soft)", margin: 0, padding: "12px 14px", fontStyle: "italic" }}>No habits in this category</p>
          : <div>{children}</div>
        }
      </div>
    </div>
  );

  return (
    // hs-page: see-through cards on gradient backgrounds (GLASS_CARD_CSS, App.jsx).
    <div className={section === "habits" ? "hs-page" : undefined} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {historyAmountSheet && (() => {
        const sh = habits.find(x => x.id === historyAmountSheet.habitId);
        if (!sh) return null;
        const d = historyAmountSheet.date;
        const shCat = categories?.find(c => c.id === sh.goalId);
        return (
          <AddAmountSheet habit={goalOn(sh, d)} rawLog={habitLogs[d]?.[sh.id]} color={shCat?.color || sh.color}
            dateLabel={new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            onLog={(v) => {
              setHabitLogs(prev => ({ ...prev, [d]: { ...(prev[d] || {}), [sh.id]: v } }));
              setHistoryEditedItems(prev => ({ ...prev, [`${d}:${sh.id}`]: true }));
            }}
            onClose={() => setHistoryAmountSheet(null)} />
        );
      })()}
      {/* Habit Tracker — Category-Based */}
      {section === "habits" && <Card title="🗓️ Habit Tracker" className="graph-section-frost" headerRight={<div ref={setTrackerNavSlot} />}>
        {renderViewSwitch(trackerView, setTrackerView, () => { setTargetWeekOffset(0); setPhMonthOffset(0); setPhYearOffset(0); })}
        {habits.length === 0
          ? <p style={S.empty}>Add habits to see your tracker</p>
          : (() => {
            // Tracker card order: daily habits first, then specific-day habits,
            // then every "counted" type (count / monthly-count / yearly-count)
            // lumped together last. Stable sort keeps each group's original
            // relative order intact.
            // Habits of one category sit together (Jade, 2026-10-17), in the
            // Categories order, uncategorized last; the frequency order applies
            // inside each category.
            const FREQ_RANK = { daily: 0, specific: 1, interval: 1, count: 2, monthly: 2, yearly: 2 };
            const allHabits = habits.slice().sort((a, b) => catRank(a) - catRank(b) || (FREQ_RANK[a.frequencyType] ?? 3) - (FREQ_RANK[b.frequencyType] ?? 3));
            const dailyHabits = habits.filter(h => h.frequencyType === "daily");
            const specificHabits = habits.filter(h => h.frequencyType === "specific");
            const countHabits = habits.filter(h => h.frequencyType === "count");
            const monthlyHabits = habits.filter(h => h.frequencyType === "monthly");
            // ── Build start-date-relative dates for each habit ──────────────
            // Weekly: D1-D7 from habit createdAt
            // Monthly: D1-D30 from habit createdAt
            // These are per-habit, computed inline below

            const isMonthly = trackerView === "monthly";
            const isYearlyP = trackerView === "yearly";
            const COL_W = 20;
            return (
              <div>
                {(isMonthly || isYearlyP) && (
                  <style>{`
                    .clarity-monthly-scroll::-webkit-scrollbar { height: 6px; }
                    .clarity-monthly-scroll::-webkit-scrollbar-track { background: #ffffff; border-radius: 3px; }
                    .clarity-monthly-scroll::-webkit-scrollbar-thumb { background: #f4a7b9; border-radius: 3px; }
                    .clarity-monthly-scroll::-webkit-scrollbar-thumb:hover { background: #d4789a; }
                    @media (hover: none) { .clarity-monthly-scroll::-webkit-scrollbar { display: none; } }
                    /* Desktop keeps the pink scrollbar — a mouse user needs something to
                       grab, and something telling them the grid scrolls sideways.
                       Deliberately scoped to desktop: ANY ::-webkit-scrollbar rule
                       matching an element puts Chromium into custom-scrollbar mode,
                       where the bar is drawn persistently and takes real layout space
                       instead of the transient overlay indicator Android would use. */
                    @media (min-width: 1024px) {
                      .clarity-yearly-scroll::-webkit-scrollbar { height: 6px; }
                      .clarity-yearly-scroll::-webkit-scrollbar-track { background: #ffffff; border-radius: 3px; }
                      .clarity-yearly-scroll::-webkit-scrollbar-thumb { background: #f4a7b9; border-radius: 3px; }
                      .clarity-yearly-scroll::-webkit-scrollbar-thumb:hover { background: #d4789a; }
                    }
                    /* Mobile (native app + the live site on a phone): no scrollbar at
                       all, still swipeable. The !important + width/height 0 + display
                       none combination is the app's proven hide pattern (see App.jsx)
                       — needed because the global 4px ::-webkit-scrollbar rule there
                       matches every scroller and otherwise wins. */
                    @media (max-width: 1023.98px) {
                      .clarity-yearly-scroll, .clarity-yearly-scroll * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                      .clarity-yearly-scroll::-webkit-scrollbar, .clarity-yearly-scroll *::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
                    }
                  `}</style>
                )}



                {/* ── Per Habit section ── */}
                <div ref={perHabitRef} />

                {/* YEARLY VIEW - GitHub-style heatmap, each habit from its own createdAt */}
                {isYearlyP ? (() => {
                  const BOX = 11; const GAP = 3; const CELL = BOX + GAP;
                  const DAY_LABELS = ["Mo","Tu","We","Th","Fr","Sa","Su"];
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {/* ── Yearly nav header ── */}
                      {(() => {
                        const phIsCurrentYear = phYearOffset === 0;
                        // Earliest reachable window: back to Year 1 of the OLDEST
                        // habit. "<" is disabled once we're already there (no prior
                        // year exists); ">" is always enabled (you can look ahead).
                        const todayMsNav = new Date(fmt(now()) + "T00:00:00").getTime();
                        const maxYearWindow = Math.max(0, ...allHabits.map(h => {
                          const s = h.habitStartDate || (h.createdAt ? localDateStr(h.createdAt) : fmt(now()));
                          return Math.floor((todayMsNav - new Date(s + "T00:00:00").getTime()) / (365 * 86400000));
                        }));
                        const phCanGoBack = phYearOffset > -maxYearWindow;
                        const phYearLabel = phIsCurrentYear
                          ? "Current year"
                          : `${Math.abs(phYearOffset)} year${Math.abs(phYearOffset) > 1 ? "s" : ""} ${phYearOffset < 0 ? "ago" : "ahead"}`;
                        const phNavBtnStyleY = {
                          background: "none", border: "none", borderRadius: 10,
                          width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", color: "var(--c-text-mid)", flexShrink: 0,
                        };
                        return (
                          <TrackerNav>
                            <button style={{ ...phNavBtnStyleY, opacity: phCanGoBack ? 1 : 0.3, cursor: phCanGoBack ? "pointer" : "default" }} onClick={() => { if (phCanGoBack) setPhYearOffset(o => o - 1); }} title="Previous year"><ChevronLeft size={15} /></button>
                            <div style={{ textAlign: "center" }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)" }}>{phYearLabel}</div>
                              {phIsCurrentYear
                                ? <div style={{ fontSize: 10, color: "var(--c-text-accent)", fontWeight: 600, marginTop: 1 }}>This Year</div>
                                : <button onClick={() => setPhYearOffset(0)} style={{ background: "none", border: "none", fontSize: 10, color: "var(--c-text-accent)", cursor: "pointer", fontWeight: 600, padding: 0, marginTop: 1, fontFamily: "inherit" }}>↩ Back to current</button>
                              }
                            </div>
                            <button style={phNavBtnStyleY} onClick={() => setPhYearOffset(o => o + 1)} title="Next year"><ChevronRight size={15} /></button>
                          </TrackerNav>
                        );
                      })()}
                      {(() => {
                      const isRestrY = (h) => h.habitType === "nottodo" || h.habitType === "limited";
                      const yTodo = allHabits.filter(h => !isRestrY(h));
                      const yRestricted = allHabits.filter(isRestrY);
                      const yGroupLabel = (label, c) => (
                        <div key={`ylbl-${label}`} style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0 2px" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: c, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</span>
                          <div style={{ flex: 1, height: 1, background: `${c}30` }} />
                        </div>
                      );
                      const renderYearCard = (h) => {
                        const cat = categories.find(c => c.id === h.goalId);
                        const color = vividPastel(cat?.color || h.color);
                        const startStr = h.habitStartDate || (h.createdAt ? localDateStr(h.createdAt) : fmt(now()));
                        const startMs = new Date(startStr + "T00:00:00").getTime();
                        const todayMsY = new Date(fmt(now()) + "T00:00:00").getTime();
                        const currentYearWindow = Math.max(0, Math.floor((todayMsY - startMs) / (365 * 86400000)));
                        const yearWindowStartMs = startMs + (currentYearWindow + phYearOffset) * 365 * 86400000;
                        const startDate = new Date(yearWindowStartMs);
                        const startDow = (startDate.getDay() + 6) % 7; // 0=Mon…6=Sun
                        const totalDays = 365;
                        const totalCols = Math.ceil((startDow + totalDays) / 7);
                        const grid = Array.from({ length: totalCols }, () => Array(7).fill(null));
                        for (let i = 0; i < totalDays; i++) {
                          const d = new Date(startDate.getTime() + i * 86400000);
                          const dow = (d.getDay() + 6) % 7;
                          const col = Math.floor((startDow + i) / 7);
                          grid[col][dow] = fmt(d);
                        }
                        // M1-M12 label positions: every 30 days from habit start. Deliberately
                        // NOT HABIT_PERIOD_DAYS — these are a calendar-MONTH-NAME ruler
                        // laid over a 365-day grid (see getMonthLabel), and 12 x 28 would
                        // only span 336 days, leaving the last four weeks unlabelled.
                        const monthCols = {};
                        for (let mi = 0; mi < 12; mi++) {
                          monthCols[mi] = Math.floor((startDow + mi * 30) / 7);
                        }
                        // Label: "M1 (May)", "M2 (Jun)" etc — habit-relative index + actual calendar month name
                        const getMonthLabel = (mi) => {
                          const d = new Date(startDate.getTime() + mi * 30 * 86400000);
                          return MONTHS[d.getMonth()].slice(0, 3);
                        };
                        // Build col->label map
                        const colMonthLabel = {};
                        Object.entries(monthCols).forEach(([mi, ci]) => {
                          colMonthLabel[ci] = getMonthLabel(parseInt(mi));
                        });
                        // Completed / total counts from start date forward (past days only)
                        const allDatesInGrid = grid.flat().filter(Boolean);
                        const pastDates = allDatesInGrid.filter(d => d <= todayStr() && d >= startStr);
                        const isCountHabit = h.frequencyType === "count";
                        const dueDates = isCountHabit ? [] : pastDates.filter(d => isHabitDueOn(h, d));
                        const completedCount = isCountHabit
                          ? pastDates.filter(d => habitLogs[d]?.[h.id]).length
                          : dueDates.filter(d => { const _v = habitLogs[d]?.[h.id]; const _t = goalOn(h, d).dailyCount || 1; return typeof _v === "number" ? _v >= _t : !!_v; }).length;
                        const totalDue = isCountHabit
                          ? Math.round(pastDates.reduce((a, d) => a + (goalOn(h, d).weeklyCount || 1) / 7, 0))
                          : dueDates.length;
                        return (
                          <div key={h.id} className="habit-track-card hs-glass"
                            onClick={() => setHabitCalendarModal({ habit: h, month: now().getMonth(), year: now().getFullYear() })}
                            style={{ "--trk-c": color, border: `1.5px solid ${color}33`, borderRadius: 14, overflow: "hidden", background: "var(--c-bg-card)", cursor: "pointer",
                              // Each card renders ~370 grid cells (53 weeks x 7 days); with several
                              // habits stacked here that's thousands of live DOM nodes the browser
                              // keeps laid out and painted even though only 1-2 cards are on screen,
                              // which is what made scrolling feel sluggish. content-visibility skips
                              // that work for off-screen cards; containIntrinsicSize reserves their
                              // measured height so the list doesn't jump as they come into view.
                              contentVisibility: "auto", containIntrinsicSize: "0 165px" }}>
                            {/* Header */}
                            <div style={{ background: `${color}15`, padding: "8px 12px", borderBottom: `1px solid ${color}22`, display: "flex", alignItems: "center", gap: 8 }}>
                              <HabitIcon h={h} size={16} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)" }}>{h.name}{goalTag(h) ? <span style={{ fontSize: 10, fontWeight: 700, color: vibrantAccent(color), marginLeft: 4 }}>({goalTag(h)})</span> : null}</div>
                              </div>
                              <span style={{ fontSize: 10, color: color, fontWeight: 700, background: `${color}18`, borderRadius: 8, padding: "2px 8px" }}>Year {currentYearWindow + phYearOffset + 1}</span>
                            </div>
                            {/* Heatmap */}
                            <div className="clarity-yearly-scroll" style={{ padding: "8px 10px 10px 10px", overflowX: "auto", WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y", overscrollBehaviorX: "contain", willChange: "scroll-position" }}>
                              {/* Calendar month labels row */}
                              <div style={{ display: "flex", marginBottom: 3, marginLeft: 0 }}>
                                {Array.from({ length: totalCols }, (_, ci) => (
                                  <div key={ci} style={{ width: CELL, flexShrink: 0, fontSize: 8, color: "var(--c-text-soft)", fontWeight: 600, whiteSpace: "nowrap", overflow: "visible" }}>
                                    {colMonthLabel[ci] || ""}
                                  </div>
                                ))}
                              </div>
                              {/* Grid rows */}
                              <div style={{ display: "flex", gap: 0 }}>
                                <div style={{ display: "flex", gap: GAP, minWidth: "max-content" }}>
                                  {grid.map((col, ci) => (
                                    <div key={ci} style={{ display: "flex", flexDirection: "column", gap: GAP }}>
                                      {col.map((dateStr, ri) => {
                                        if (!dateStr) return <div key={ri} style={{ width: BOX, height: BOX, flexShrink: 0 }} />;
                                        const isFuture = dateStr > todayStr();
                                        const isRestricted = h.habitType === "nottodo" || h.habitType === "limited";
                                        const isBudgetY = isRestricted && !!h.allowanceMode;
                                        const isDue = (h.frequencyType === "count" ? !!habitLogs[dateStr]?.[h.id] : isHabitDueOn(h, dateStr)) && dateStr >= startStr;
                                        const _logVal4885 = habitLogs[dateStr]?.[h.id];
                                        const _target4885 = goalOn(h, dateStr).dailyCount || 1;
                                        const _fullyDone4885 = typeof _logVal4885 === "number" ? _logVal4885 >= _target4885 : !!_logVal4885;
                                        const checked = !isFuture && isDue && (
                                          isBudgetY
                                            ? (h.attemptLog || []).some(d => d === dateStr)
                                            : isRestricted
                                              ? isHabitDoneOn(h, habitLogs, dateStr, habitSkips)
                                              : _fullyDone4885
                                        );
                                        const isToday = dateStr === todayStr();
                                        const d = new Date(dateStr + "T00:00:00");
                                        return (
                                          <div
                                            key={ri}
                                            className={isDue && isFuture ? "trk-future" : undefined}
                                            onClick={(e) => {
                                              if (isFuture || !isDue) return; // let it bubble to the card → opens current month
                                              e.stopPropagation();
                                              setHabitCalendarModal({ habit: h, month: d.getMonth(), year: d.getFullYear() });
                                            }}
                                            style={{
                                              width: BOX, height: BOX, borderRadius: 2, flexShrink: 0,
                                              cursor: "pointer",
                                              background: !isDue ? "#eae6ee" : isFuture ? FUTURE_BLOCK_BG : isRestricted ? restrictedDayColor(h, dateStr, habitSkips) : checked ? color : "#c8c0d0",
                                              outline: isToday ? "2px solid #c0a0b8" : "none",
                                              outlineOffset: 1,
                                              opacity: !isDue ? 0.3 : 1,
                                              transition: "background 0.12s",
                                            }}
                                          />
                                        );
                                      })}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      };
                      return (
                        <>
                          {yTodo.length > 0 && (<>{yRestricted.length > 0 && yGroupLabel("To-Do", "#6abf7a")}{yTodo.map(renderYearCard)}</>)}
                          {yRestricted.length > 0 && (<>{yGroupLabel("Restricted", "#e07090")}{yRestricted.map(renderYearCard)}</>)}
                        </>
                      );
                      })()}
                      {allHabits.length === 0 && (
                        <p style={{ fontSize: 12, color: "var(--c-text-soft)", margin: 0, fontStyle: "italic" }}>No habits to show</p>
                      )}
                      {heatmapTooltip && (
                        <div style={{ position: "fixed", bottom: "calc(100px + var(--sab, 0px))", left: "50%", transform: "translateX(-50%)", zIndex: 999, background: heatmapTooltip.checked ? heatmapTooltip.color : "#6a5a70", color: "#fff", borderRadius: 20, padding: "8px 18px", fontSize: 12, fontWeight: 700, boxShadow: "0 4px 18px rgba(0,0,0,0.22)", display: "flex", alignItems: "center", gap: 8, pointerEvents: "none", whiteSpace: "nowrap" }}>
                          <span style={{ fontSize: 14 }}>{heatmapTooltip.checked ? "✓" : "✗"}</span>
                          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <span style={{ fontSize: 12, fontWeight: 700 }}>{heatmapTooltip.label}</span>
                            <span style={{ fontSize: 10, opacity: 0.85, fontWeight: 500 }}>{heatmapTooltip.checked ? "Completed" : "Not completed"}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })() : (
                (() => {
                  // Weekly: D1-D7 from each habit's createdAt
                  // Monthly: D1-D30 from each habit's createdAt
                  // Each habit gets its own relative date range
                  const trackedHabits = allHabits;

                  const getHabitDates = (h) => {
                    // 7-day windows anchored to THIS habit's own start date — the first
                    // block always lands on the start date's weekday (not Monday). Window 0
                    // is [start, start+6], window 1 is [start+7, start+13], etc. We default
                    // to the window that contains today, so the current week's boxes show
                    // immediately, and targetWeekOffset steps between windows.
                    const todayF = fmt(now());
                    const todayMs = new Date(todayF + "T00:00:00").getTime();
                    const startStr = h.habitStartDate || (h.createdAt ? localDateStr(h.createdAt) : todayF);
                    const startMs = new Date(startStr + "T00:00:00").getTime();
                    const currentWindow = Math.max(0, Math.floor((todayMs - startMs) / (7 * 86400000)));
                    const windowStartMs = startMs + (currentWindow + targetWeekOffset) * 7 * 86400000;
                    return Array.from({ length: 7 }, (_, i) => {
                      const dateStr = fmt(new Date(windowStartMs + i * 86400000));
                      return { dateStr, isFutureDate: dateStr > todayF };
                    });
                  };

                  if (isMonthly) {
                    // ── Habit Radar style: 2 cards per row, 30 tiny checkboxes each ──
                    return (
                      <>
                        <style>{`
                          .clarity-monthly-grid {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 6px;
                          }
                          .clarity-habit-month-card {
                            background: #fff;
                            border-radius: 12px;
                            padding: 0 0 7px 0;
                            overflow: hidden;
                            display: flex;
                            flex-direction: column;
                            gap: 6px;
                            cursor: pointer;
                            box-shadow: 0 1px 4px rgba(180,100,140,0.07);
                            min-width: 0;
                          }
                          /* Tinted band, like the Weekly / Yearly cards (Jade, 2026-10-19). */
                          .clarity-habit-month-card .hm-header {
                            display: flex;
                            align-items: center;
                            gap: 4px;
                            padding: 5px 6px;
                          }
                          .clarity-habit-month-card .hm-emoji-wrap {
                            width: 24px;
                            height: 24px;
                            border-radius: 7px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            flex-shrink: 0;
                          }
                          .clarity-habit-month-card .hm-name {
                            font-size: 13px;
                            font-weight: 700;
                            color: #3a2040;
                            line-height: 1.25;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            flex: 1;
                            min-width: 0;
                          }
                          .clarity-habit-month-card .hm-grid {
                            padding: 0 6px;
                            display: grid;
                            grid-template-columns: repeat(6, 1fr);
                            gap: 5px;
                          }
                          .clarity-habit-month-card .hm-cell {
                            aspect-ratio: 1;
                            border-radius: 5px;
                            box-sizing: border-box;
                          }
                        `}</style>
                        {/* ── Per-habit monthly nav header ── */}
                        {(() => {
                          const phIsCurrentMonth = phMonthOffset === 0;
                          const phMonthLabel = phIsCurrentMonth
                            ? "Current period"
                            : `${Math.abs(phMonthOffset)} period${Math.abs(phMonthOffset) > 1 ? "s" : ""} ${phMonthOffset < 0 ? "ago" : "ahead"}`;
                          const phNavBtnStyleM = {
                            background: "none", border: "none", borderRadius: 10,
                            width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", color: "var(--c-text-mid)", flexShrink: 0,
                          };
                          // "<" is disabled once going back would land before any habit
                          // existed yet (no earlier period window has data); ">" is
                          // always enabled so users can look ahead.
                          const todayMsM = new Date(fmt(now()) + "T00:00:00").getTime();
                          const maxMonthWindow = Math.max(0, ...allHabits.map(h => {
                            const s = h.habitStartDate || (h.createdAt ? localDateStr(h.createdAt) : fmt(now()));
                            return Math.floor((todayMsM - new Date(s + "T00:00:00").getTime()) / (HABIT_PERIOD_DAYS * 86400000));
                          }));
                          const phCanGoBackMonth = allHabits.length > 0 && phMonthOffset > -maxMonthWindow;
                          return (
                            <TrackerNav>
                              <button style={{ ...phNavBtnStyleM, opacity: phCanGoBackMonth ? 1 : 0.3, cursor: phCanGoBackMonth ? "pointer" : "default" }} onClick={() => { if (phCanGoBackMonth) setPhMonthOffset(o => o - 1); }} title="Previous period"><ChevronLeft size={15} /></button>
                              <div style={{ textAlign: "center" }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)" }}>{phMonthLabel}</div>
                                {phIsCurrentMonth
                                  ? <div style={{ fontSize: 10, color: "var(--c-text-accent)", fontWeight: 600, marginTop: 1 }}>Current</div>
                                  : <button onClick={() => setPhMonthOffset(0)} style={{ background: "none", border: "none", fontSize: 10, color: "var(--c-text-accent)", cursor: "pointer", fontWeight: 600, padding: 0, marginTop: 1, fontFamily: "inherit" }}>↩ Back to current</button>
                                }
                              </div>
                              <button style={phNavBtnStyleM} onClick={() => setPhMonthOffset(o => o + 1)} title="Next period"><ChevronRight size={15} /></button>
                            </TrackerNav>
                          );
                        })()}
                        {(() => {
                          const isRestrM = (h) => h.habitType === "nottodo" || h.habitType === "limited";
                          const mTodo = trackedHabits.filter(h => !isRestrM(h));
                          const mRestricted = trackedHabits.filter(isRestrM);
                          const mGroupLabel = (label, c) => (
                            <div key={`mlbl-${label}`} style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0 4px" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: c, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</span>
                              <div style={{ flex: 1, height: 1, background: `${c}30` }} />
                            </div>
                          );
                          const renderMonthCard = (h) => {
                              const cat = categories.find(c => c.id === h.goalId);
                              const color = vividPastel(cat?.color || h.color);
                              const startStr = h.habitStartDate || (h.createdAt ? localDateStr(h.createdAt) : fmt(now()));
                              const originMs = new Date(startStr + "T00:00:00").getTime();
                              const todayMs = new Date(fmt(now()) + "T00:00:00").getTime();
                              const currentMonthWindow = Math.max(0, Math.floor((todayMs - originMs) / (HABIT_PERIOD_DAYS * 86400000)));
                              const monthWindowStartMs = originMs + (currentMonthWindow + phMonthOffset) * HABIT_PERIOD_DAYS * 86400000;
                              const boxes = Array.from({ length: HABIT_PERIOD_DAYS }, (_, i) => {
                                const ms = monthWindowStartMs + i * 86400000;
                                const dateStr = fmt(new Date(ms));
                                // A day off (excluded date) is drawn and counted like a future day.
                                const isFuture = ms > todayMs || isHabitExcludedOn(h, dateStr);
                                const isRestricted = h.habitType === "nottodo" || h.habitType === "limited";
                                const isBudgetM = isRestricted && !!h.allowanceMode;
                                const _logVal5032 = habitLogs[dateStr]?.[h.id];
                                const _target5032 = goalOn(h, dateStr).dailyCount || 1;
                                const _fullyDone5032 = typeof _logVal5032 === "number" ? _logVal5032 >= _target5032 : !!_logVal5032;
                                const checked = !isFuture && (
                                  isBudgetM
                                    ? (h.attemptLog || []).some(d => d === dateStr)
                                    : isRestricted
                                      ? isHabitDoneOn(h, habitLogs, dateStr, habitSkips)
                                      : _fullyDone5032
                                );
                                const restrictedBg = isRestricted && !isFuture ? restrictedDayColor(h, dateStr, habitSkips) : null;
                                return { dateStr, checked, isFuture, isRestricted, restrictedBg };
                              });
                              const pastBoxes = boxes.filter(b => !b.isFuture);
                              const completionPct = pastBoxes.length > 0 ? Math.round((pastBoxes.filter(b => b.checked).length / pastBoxes.length) * 100) : null;
                              const pctColor = completionPct === null ? "#c0a0b8" : completionPct >= 80 ? "#4caf50" : completionPct >= 50 ? "#f5a623" : "#e57373";
                              return (
                                <div key={h.id}
                                  className="clarity-habit-month-card hs-glass"
                                  onClick={() => setHabitCalendarModal({ habit: h, month: now().getMonth(), year: now().getFullYear() })}
                                  style={{ "--trk-c": color, border: `1.5px solid ${color}33` }}>
                                  <div className="hm-header" style={{ background: `${color}15`, borderBottom: `1px solid ${color}22` }}>
                                    <div className="hm-emoji-wrap" style={{ background: `${color}22` }}>
                                      <HabitIcon h={h} size={16} />
                                    </div>
                                    <span className="hm-name">{h.name}{goalTag(h) ? <span style={{ fontSize: 10, fontWeight: 700, color: vibrantAccent(h.color || "#d4789a"), marginLeft: 3 }}>({goalTag(h)})</span> : null}</span>
                                  </div>
                                  <div className="hm-grid">
                                    {boxes.map(({ dateStr, checked, isFuture, isRestricted, restrictedBg }, i) => (
                                      <div key={i} className={isFuture ? "hm-cell trk-future" : "hm-cell"} style={{
                                        background: isFuture ? FUTURE_BLOCK_BG : isRestricted ? restrictedBg : checked ? color : "#ddd8ea",
                                      }} />
                                    ))}
                                  </div>
                                </div>
                              );
                          };
                          if (trackedHabits.length === 0) return <p style={{ ...S.empty }}>Add habits to see your tracker</p>;
                          return (
                            <>
                              {mTodo.length > 0 && (<>{mRestricted.length > 0 && mGroupLabel("To-Do", "#6abf7a")}<div className="clarity-monthly-grid">{mTodo.map(renderMonthCard)}</div></>)}
                              {mRestricted.length > 0 && (<>{mGroupLabel("Restricted", "#e07090")}<div className="clarity-monthly-grid">{mRestricted.map(renderMonthCard)}</div></>)}
                            </>
                          );
                        })()}
                      </>
                    );
                  }

                  // Individual per-habit cards — weekly view
                  // Monthly & yearly-frequency habits excluded; they belong in the month/year views
                  const weeklyTrackedHabits = trackedHabits.filter(h => h.frequencyType !== "monthly" && h.frequencyType !== "yearly");
                  const weeklyRestrictedHabits = weeklyTrackedHabits.filter(h => h.habitType === "nottodo" || h.habitType === "limited");
                  const weeklyTodoHabits = weeklyTrackedHabits.filter(h => h.habitType !== "nottodo" && h.habitType !== "limited");

                  const DAY_INITIALS = { 0: "SU", 1: "M", 2: "T", 3: "W", 4: "TH", 5: "F", 6: "SA" };
                  const getFreqLabel = (h) => {
                    if (h.frequencyType === "daily") return "Daily";
                    if (h.frequencyType === "interval") return intervalLabel(h);
                    if (h.frequencyType === "count") return `${h.weeklyCount || 1}× this week`;
                    if (h.frequencyType === "yearly") return (h.yearlyMode || "count") === "count" ? `${h.yearlyCount || 1}× this year` : "Yearly";
                    if (h.frequencyType === "specific" && h.dayLabels === "named") {
                      const sorted = [...(h.days || [])].sort((a, b) => a - b);
                      return sorted.map(d => DAY_INITIALS[d] || d).join(", ");
                    }
                    return "Every day";
                  };

                  if (weeklyTrackedHabits.length === 0) {
                    return <p style={S.empty}>Add habits to see your tracker</p>;
                  }

                  // ── Shared habit card renderer ──
                  const isRestrictedHabit = (h) => h.habitType === "nottodo" || h.habitType === "limited";

                  const renderHabitCard = (h) => {
                        const cat = categories.find(c => c.id === h.goalId);
                        const color = vividPastel(cat?.color || h.color);
                        const habitDates = getHabitDates(h);
                        const freqLabel = getFreqLabel(h);
                        const hIsRestricted = isRestrictedHabit(h);

                        // ── Budget / counted restricted habit ──────────────────────────────
                        if (h.allowanceMode) {
                          const max = h.allowanceMax || 1;
                          const period = (h.allowanceMode === "month" || h.allowanceMode === "monthDates") ? "month" : "week";
                          const todayF = fmt(now());

                          // Period start for counting attempts — anchored to the
                          // habit's own start date, same as every other habit
                          // visualization (and as the dot row below).
                          const usedThisPeriod = allowanceUsed(h, todayF);
                          const over = usedThisPeriod > max;
                          const atLimit = usedThisPeriod >= max;
                          const tone = over
                            ? { c: "#A32D2D", bg: "#FCEBEB", bd: "#F09595" }
                            : atLimit
                              ? { c: "#854F0B", bg: "#FEF8E7", bd: "#F5C842" }
                              : { c: "#3B6D11", bg: "#F0FAF0", bd: "#AFD89A" };

                          // Same 7 dates as regular habit cards — anchored to habit's start date
                          const dotDates = habitDates.map(d => d.dateStr);

                          return (
                            <div key={h.id} className="habit-track-card hs-glass"
                              onClick={() => setHabitCalendarModal({ habit: h, month: now().getMonth(), year: now().getFullYear() })}
                              style={{ "--trk-c": color, border: `1.5px solid ${color}33`, borderRadius: 14, overflow: "hidden", background: "var(--c-bg-card)", cursor: "pointer" }}>
                              {/* Header */}
                              <div style={{ background: `${color}12`, padding: "9px 12px 8px 12px", borderBottom: `1px solid ${color}22`, display: "flex", alignItems: "center", gap: 9 }}>
                                <div style={{ width: 32, height: 32, borderRadius: 10, background: `${color}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <HabitIcon h={h} size={17} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</div>
                                </div>
                                <span style={{ fontSize: 10, color, fontWeight: 700, background: `${color}18`, borderRadius: 8, padding: "3px 8px", flexShrink: 0 }}>
                                  up to {max}× / {period}
                                </span>
                              </div>
                              {/* Body */}
                              <div style={{ padding: "10px 12px 12px 12px" }}>
                                {/* Dots row — every day reads as "on track" (green) since a clean
                                    (unused) day never breaks the budget. A used day shows the running
                                    count of uses so far this period (e.g. "1", "2", "3" as you spend
                                    your budget); it only flips to amber once a use pushes you over the
                                    limit, so users can't mistake "using the budget as intended" for
                                    "breaking the habit". Same size as the to-do tracker blocks. */}
                                <div style={{ display: "flex", gap: isDesktop ? 4 : 4, justifyContent: "space-between" }}>
                                  {dotDates.map((dateStr, di) => {
                                    const isFuture = dateStr > todayF || isHabitExcludedOn(h, dateStr); // a day off reads like a future day
                                    const isToday = dateStr === todayF;
                                    const cumulativeThroughDay = allowanceUsed(h, dateStr);
                                    const usedOnDay = (h.attemptLog || []).some(d => d === dateStr);
                                    const bgColor = isFuture ? FUTURE_BLOCK_BG : restrictedDayColor(h, dateStr, habitSkips);
                                    return (
                                      <div key={di} className={isFuture ? "trk-future" : undefined} style={{
                                        flex: "1 1 0", minWidth: 0, maxWidth: isDesktop ? 40 : 30, aspectRatio: "1 / 1",
                                        borderRadius: 6,
                                        background: bgColor,
                                        outline: isToday ? `2.5px solid ${color}` : "none",
                                        outlineOffset: 2,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        boxSizing: "border-box", transition: "background 0.15s",
                                      }}>
                                        {usedOnDay && !isFuture && (
                                          <span style={{ fontSize: 11, fontWeight: 800, color: "#fff" }}>{cumulativeThroughDay}</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                {/* Budget status — plain text, no pill */}
                                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: tone.c }}>
                                    {over ? `🌧️ over by ${usedThisPeriod - max}` : `☀️ ${max - usedThisPeriod} left this ${period}`}
                                  </span>
                                  <span style={{ fontSize: 11, color: "var(--c-text-soft)" }}>{usedThisPeriod}/{max} used</span>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={h.id} className="habit-track-card hs-glass"
                            onClick={() => setHabitCalendarModal({ habit: h, month: now().getMonth(), year: now().getFullYear() })}
                            style={{ "--trk-c": color, border: `1.5px solid ${color}33`, borderRadius: 14, overflow: "hidden", background: "var(--c-bg-card)", cursor: "pointer" }}>
                            {/* Card header */}
                            <div
                              style={{ background: `${color}12`, padding: "9px 12px 8px 12px", borderBottom: `1px solid ${color}22`, display: "flex", alignItems: "center", gap: 9 }}
                            >
                              <div style={{ width: 32, height: 32, borderRadius: 10, background: `${color}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <HabitIcon h={h} size={17} />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}{goalTag(h) ? <span style={{ fontSize: 10, fontWeight: 700, color: vibrantAccent(h.color || "#d4789a"), marginLeft: 4 }}>({goalTag(h)})</span> : null}</div>
                              </div>
                              <span style={{ fontSize: 10, color: color, fontWeight: 700, background: `${color}18`, borderRadius: 8, padding: "3px 8px", flexShrink: 0 }}>
                                {freqLabel}
                              </span>
                            </div>
                            {/* Blocks — solid colored, read-only, click opens calendar modal */}
                            <div style={{ padding: "10px 12px 12px 12px" }}>
                              {hIsRestricted && (() => {
                                const DN = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
                                let label = null;
                                if (h.habitType === "nottodo") {
                                  label = "🚫 Restricted every day";
                                } else if (h.habitType === "limited" && h.limitedInterval && !h.allowanceMode) {
                                  label = `🚫 Not allowed: ${intervalLabel(h).toLowerCase()} · allowed other days`;
                                } else if (h.habitType === "limited") {
                                  const off = limitedOffDays(h);
                                  label = off.length === 0
                                    ? "🚫 Restricted every day"
                                    : `🚫 Not allowed: ${off.map(d => DN[d]).join(", ")} · allowed other days`;
                                }
                                return label ? <p style={{ fontSize: 10, color: "var(--c-text-soft)", margin: "0 0 8px", lineHeight: 1.4 }}>{label}</p> : null;
                              })()}
                              <div style={{ display: "flex", gap: isDesktop ? 4 : 4, justifyContent: "space-between" }}>
                                {habitDates.map(({ dateStr, isFutureDate }, di) => {
                                  const _logValW = habitLogs[dateStr]?.[h.id];
                                  const _targetW = goalOn(h, dateStr).dailyCount || 1;
                                  const _fullyDoneW = typeof _logValW === "number" ? _logValW >= _targetW : !!_logValW;
                                  const checked = !isFutureDate && (hIsRestricted ? isHabitDoneOn(h, habitLogs, dateStr, habitSkips) : _fullyDoneW);
                                  const isToday = dateStr === todayStr();
                                  const d = new Date(dateStr + "T00:00:00");
                                  const habitStart = h.habitStartDate || localDateStr(h.createdAt) || "2000-01-01";
                                  // A day off (excluded date) reads like a future day: nothing expected.
                                  const isBeforeStart = dateStr < habitStart || isHabitExcludedOn(h, dateStr);
                                  const blockBg = (() => {
                                    if (isFutureDate || isBeforeStart) return FUTURE_BLOCK_BG;
                                    if (hIsRestricted) return restrictedDayColor(h, dateStr, habitSkips);
                                    return checked ? color : "#e8e0f0";
                                  })();
                                  return (
                                    <div
                                      key={di}
                                      className={(isFutureDate || isBeforeStart) ? "trk-future" : undefined}
                                      onClick={(e) => { e.stopPropagation(); setHabitCalendarModal({ habit: h, month: d.getMonth(), year: d.getFullYear() }); }}
                                      style={{
                                        flex: "1 1 0",
                                        minWidth: 0,
                                        maxWidth: isDesktop ? 40 : 30,
                                        aspectRatio: "1 / 1",
                                        borderRadius: 6,
                                        background: blockBg,
                                        boxSizing: "border-box",
                                        cursor: "pointer",
                                        outline: isToday ? `2.5px solid ${color}` : "none",
                                        outlineOffset: 2,
                                        transition: "background 0.15s",
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                  };

                  // ── Section divider label ──
                  const TrackerGroupLabel = ({ label, emoji, color }) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 2px" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: 0.5, textTransform: "uppercase" }}>{emoji} {label}</span>
                      <div style={{ flex: 1, height: 1, background: `${color}30` }} />
                    </div>
                  );

                  // ── Per-habit week nav header (shares targetWeekOffset with completion target) ──
                  // Each habit's blocks are anchored to its own start date, so windows differ
                  // per habit — the header therefore shows a RELATIVE label rather than one
                  // shared calendar date range.
                  const phIsCurrentWeek = targetWeekOffset === 0;
                  const phLabel = phIsCurrentWeek
                    ? "Current week"
                    : `${Math.abs(targetWeekOffset)} week${Math.abs(targetWeekOffset) > 1 ? "s" : ""} ${targetWeekOffset < 0 ? "ago" : "ahead"}`;
                  const phNavBtnStyle = {
                    background: "none", border: "none", borderRadius: 10,
                    width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", color: "var(--c-text-mid)", flexShrink: 0,
                  };
                  // "<" is disabled once going back would land before any habit
                  // existed yet; ">" is always enabled so users can look ahead.
                  const todayMsW = new Date(fmt(now()) + "T00:00:00").getTime();
                  const maxWeekWindow = Math.max(0, ...allHabits.map(h => {
                    const s = h.habitStartDate || (h.createdAt ? localDateStr(h.createdAt) : fmt(now()));
                    return Math.floor((todayMsW - new Date(s + "T00:00:00").getTime()) / (7 * 86400000));
                  }));
                  const phCanGoBackWeek = allHabits.length > 0 && targetWeekOffset > -maxWeekWindow;

                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: isDesktop ? 6 : 10 }}>

                      {/* ── Week navigation for per-habit blocks ── */}
                      <TrackerNav>
                        <button style={{ ...phNavBtnStyle, opacity: phCanGoBackWeek ? 1 : 0.3, cursor: phCanGoBackWeek ? "pointer" : "default" }} onClick={() => { if (phCanGoBackWeek) setTargetWeekOffset(o => o - 1); }} title="Previous week"><ChevronLeft size={15} /></button>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)" }}>{phLabel}</div>
                          {phIsCurrentWeek
                            ? <div style={{ fontSize: 10, color: "var(--c-text-accent)", fontWeight: 600, marginTop: 1 }}>This Week</div>
                            : <button onClick={() => setTargetWeekOffset(0)} style={{ background: "none", border: "none", fontSize: 10, color: "var(--c-text-accent)", cursor: "pointer", fontWeight: 600, padding: 0, marginTop: 1, fontFamily: "inherit" }}>↩ Back to current</button>
                          }
                        </div>
                        <button style={phNavBtnStyle} onClick={() => setTargetWeekOffset(o => o + 1)} title="Next week"><ChevronRight size={15} /></button>
                      </TrackerNav>

                      {/* ── To-Do Habits ── */}
                      {weeklyTodoHabits.length > 0 && (
                        <>
                          {weeklyRestrictedHabits.length > 0 && (
                            <TrackerGroupLabel label="To-Do" emoji="✅" color="#6abf7a" />
                          )}
                          {weeklyTodoHabits.map(h => renderHabitCard(h))}
                        </>
                      )}

                      {/* ── Restricted Habits ── */}
                      {weeklyRestrictedHabits.length > 0 && (
                        <>
                          <TrackerGroupLabel label="Restricted" emoji="🚫" color="#e07090" />
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, paddingLeft: 2 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#3B6D11" }}>
                              <span style={{ width: 10, height: 10, borderRadius: 3, background: "#7DC87A", display: "inline-block" }} />clean day
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#8B6914" }}>
                              <span style={{ width: 10, height: 10, borderRadius: 3, background: "#F5C842", display: "inline-block" }} />slipped
                            </span>
                          </div>
                          {weeklyRestrictedHabits.map(h => renderHabitCard(h))}
                        </>
                      )}

                      {weeklyTrackedHabits.length === 0 && (
                        <p style={S.empty}>Add habits to see your tracker</p>
                      )}
                    </div>
                  );
                })()
                )} {/* end isYearlyP ? ... : ... */}
              </div>
            );
          })()
        }
      </Card>}

      {/* Target Completion: its own card with its own Weekly / 4-Week / Yearly
          switch, separate from the tracker's (Jade, 2026-10-23). */}
      {section === "habits" && habits.length > 0 && <Card title="🎯 Target Completion" className="graph-section-frost">
        <div ref={completionTargetCardRef}>
          {renderViewSwitch(targetView, setTargetView, () => { setTcWeekOffset(0); setTcMonthOffset(0); setTcYearOffset(0); })}
          {(() => {
            // Re-derive habit groups in this scope (they're defined in a sibling scope above)
            const dailyHabits    = habits.filter(h => h.frequencyType === "daily");
            const specificHabits = habits.filter(h => h.frequencyType === "specific");
            const countHabits    = habits.filter(h => h.frequencyType === "count");
            const monthlyHabits  = habits.filter(h => h.frequencyType === "monthly");
            const intervalHabits = habits.filter(h => h.frequencyType === "interval");
            const isRestrictedH  = (h) => h.habitType === "nottodo" || h.habitType === "limited";

            const goalHabits = [
              ...dailyHabits,
              ...specificHabits,
              ...countHabits,
              ...intervalHabits,
              ...monthlyHabits
            ];
            // Same colours together: grouped by category (stable, so the
            // frequency order above holds inside each one). Jade, 2026-10-17.
            const allGoalHabits = goalHabits.slice().sort((a, b) => catRank(a) - catRank(b));
            const goalRestrictedHabits = allGoalHabits.filter(h => isRestrictedH(h));
            const goalTodoHabits = allGoalHabits.filter(h => !isRestrictedH(h));

            // ── Restricted-habit day helpers ─────────────────────────────────
            // A boundary applies only on its "restricted" days. nottodo = every
            // day; limited = the days NOT in allowedDays (the allowed days are
            // free, so they don't count).
            const isRestrictedDay = (h, d) => {
              if (isHabitExcludedOn(h, fmt(d))) return false; // a day off doesn't count
              const monDow = (d.getDay() + 6) % 7;
              if (h.habitType === "nottodo") return true;
              if (h.habitType === "limited") return isLimitedRestrictedOn(h, fmt(d));
              return true;
            };
            const WEEKS_PER_PERIOD = HABIT_PERIOD_DAYS / 7; // 4, exactly
            const restrictedDaysPerWeek = (h) => {
              // Budget (allowanceMode) habits: every day is a potential
              // "clean" day except the ones your allowance lets you use —
              // target = 7 minus your (period-scaled) allowance, NOT the
              // allowance itself (that's how many slips you get, not how
              // many clean days you're aiming for).
              if (h.allowanceMode === "week") return Math.max(1, 7 - (h.allowanceMax || 1));
              if (h.allowanceMode === "month") return Math.max(1, Math.round(7 - (h.allowanceMax || 1) / WEEKS_PER_PERIOD));
              if (h.habitType === "nottodo") return 7;
              if (h.habitType === "limited" && h.limitedInterval) return Math.max(1, Math.round(7 * intervalDueDaysBetween(h, "2025-01-06", "2025-12-28") / 357));
              if (h.habitType === "limited") return limitedOffDays(h).length || 7;
              return 7;
            };
            const countRestrictedDaysInRange = (h, fromMs, toMs) => {
              const hStartStr = h.habitStartDate || localDateStr(h.createdAt) || todayStr();
              const hStartMs = new Date(hStartStr + "T00:00:00").getTime();
              let count = 0;
              for (let ms = Math.max(fromMs, hStartMs); ms <= toMs; ms += 86400000) {
                if (isRestrictedDay(h, new Date(ms))) count++;
              }
              return count;
            };

            // ── Pure schedule-math targets (independent of start date) ──────────
            // Weekly: how many times this habit is due per 7-day week
            // To-Do targets are shared with the Habits home-screen widget.
            // Excluded dates (days off) in the range come off the target,
            // so a week holding Christmas still has a reachable 100%.
            const getWeeklyTarget = (h, range) => minusExcluded(h, isRestrictedH(h) ? restrictedDaysPerWeek(h) : todoWeeklyTarget(h, fmt(new Date(range.startMs))), range.startMs, range.endMs);

            // Monthly: how many times due per this habit's own period. A period is
            // HABIT_PERIOD_DAYS (28) = exactly 4 weeks, so the old 4.33 weeks-per-
            // month fudge is gone — weekly counts now divide evenly.
            const baseMonthlyTarget = (h, range) => {
              h = goalOn(h, fmt(new Date(range.startMs))); // a period is judged by the goal it started with
              if (isRestrictedH(h)) {
                // Same fix as restrictedDaysPerWeek: the "clean" target is
                // the days in this period minus the (period-scaled)
                // allowance, not the allowance amount itself.
                if (h.allowanceMode) {
                  const totalDays = countRestrictedDaysInRange(h, range.startMs, range.endMs);
                  const maxUses = h.allowanceMode === "week" ? (h.allowanceMax || 1) * WEEKS_PER_PERIOD : (h.allowanceMax || 1);
                  return Math.max(1, totalDays - maxUses);
                }
                return countRestrictedDaysInRange(h, range.startMs, range.endMs);
              }
              if (h.frequencyType === "daily") return HABIT_PERIOD_DAYS;
              if (h.frequencyType === "interval") return intervalDueDaysBetween(h, fmt(new Date(range.startMs)), fmt(new Date(range.endMs)));
              if (h.frequencyType === "count") return (h.weeklyCount || 1) * WEEKS_PER_PERIOD;
              if (h.frequencyType === "specific") return ((h.days || []).length) * WEEKS_PER_PERIOD;
              if (h.frequencyType === "monthly") {
                // "Times/month" habits: scale monthlyCount down to this
                // 28-day period instead of reading the (empty) monthDates.
                if (h.monthlyMode === "count") return Math.max(1, Math.round((h.monthlyCount || 1) * (HABIT_PERIOD_DAYS / 30)));
                return (h.monthDates || []).length || 1;
              }
              if (h.frequencyType === "yearly") return Math.max(1, Math.round(((h.yearlyCount || 1) / 52) * WEEKS_PER_PERIOD));
              return 1;
            };

            // Yearly: how many times due per this habit's own 365-day period
            const baseYearlyTarget = (h, range) => {
              h = goalOn(h, fmt(new Date(range.startMs)));
              if (isRestrictedH(h)) {
                if (h.allowanceMode) {
                  const totalDays = countRestrictedDaysInRange(h, range.startMs, range.endMs);
                  const maxUses = h.allowanceMode === "week" ? (h.allowanceMax || 1) * 52 : (h.allowanceMax || 1) * 12;
                  return Math.max(1, totalDays - maxUses);
                }
                return countRestrictedDaysInRange(h, range.startMs, range.endMs);
              }
              if (h.frequencyType === "daily") return 365;
              if (h.frequencyType === "interval") return intervalDueDaysBetween(h, fmt(new Date(range.startMs)), fmt(new Date(range.endMs)));
              if (h.frequencyType === "count") return (h.weeklyCount || 1) * 52;
              if (h.frequencyType === "specific") return (h.days || []).length * 52;
              if (h.frequencyType === "monthly") {
                // "Times/month" habits: monthlyCount × 12 months, not monthDates.
                if (h.monthlyMode === "count") return (h.monthlyCount || 1) * 12;
                return (h.monthDates || []).length * 12;
              }
              if (h.frequencyType === "yearly") return h.yearlyCount || 1;
              return 1;
            };

            // Restricted targets already leave excluded days out (via
            // countRestrictedDaysInRange → isRestrictedDay); To-Do targets are
            // schedule maths, so excluded due days are taken off here.
            const getMonthlyTarget = (h, range) => isRestrictedH(h) ? baseMonthlyTarget(h, range) : minusExcluded(h, baseMonthlyTarget(h, range), range.startMs, range.endMs);
            const getYearlyTarget = (h, range) => isRestrictedH(h) ? baseYearlyTarget(h, range) : minusExcluded(h, baseYearlyTarget(h, range), range.startMs, range.endMs);

            const todayMs = new Date(todayStr() + "T00:00:00").getTime();

            // Anchor a habit's own {weekly,monthly,yearly} period to ITS OWN start
            // date, then step by `offset` periods from the one currently active —
            // same idea as the per-habit tracker grids above (which already do this
            // for monthly/yearly), now shared by the Target Completion sections too,
            // for EVERY habit type. A shared calendar week/month/year can't represent
            // this correctly: a habit created today with a "5x this week" target
            // shouldn't be judged against Sun-Sat when it's only existed since today.
            // Shared with the Habits home-screen widget (utils/habitWeeklyGoal.js).
            const habitPeriodRange = (h, periodDays, offset) => sharedHabitPeriodRange(h, periodDays, offset, todayMs);

            // Relative nav label ("2 weeks ago" / "This Week") + back-limit, shared
            // by the three Goal sections' nav bars — no single date range can label
            // a set of habits whose own periods don't line up with each other.
            // labels (optional): { current, offset(n, dir) } — overrides the
            // generated wording for units that don't pluralise, e.g. the 4-week
            // window, where "2 4-weeks ago" has to become "8 weeks ago".
            const periodNavInfo = (offset, periodDays, habitList, unitLabel, labels = {}) => {
              const isCurrent = offset === 0;
              const nOff = Math.abs(offset);
              const dir = offset < 0 ? "ago" : "ahead";
              const label = isCurrent
                ? (labels.current || `This ${unitLabel}`)
                : (labels.offset ? labels.offset(nOff, dir) : `${nOff} ${unitLabel.toLowerCase()}${nOff > 1 ? "s" : ""} ${dir}`);
              const maxWindow = Math.max(0, ...habitList.map(h => {
                const s = h.habitStartDate || localDateStr(h.createdAt) || todayStr();
                return Math.floor((todayMs - new Date(s + "T00:00:00").getTime()) / (periodDays * 86400000));
              }));
              const canGoBack = habitList.length > 0 && offset > -maxWindow;
              return { isCurrent, label, canGoBack };
            };
            const PeriodNavBar = ({ offset, setOffset, periodDays, habitList, unitLabel, labels, navUnit }) => {
              const { isCurrent, label, canGoBack } = periodNavInfo(offset, periodDays, habitList, unitLabel, labels);
              const tipUnit = navUnit || unitLabel;
              return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px 6px 12px", borderBottom: "1px solid #f5eaf0", background: "var(--c-bg-soft)" }}>
                  <button style={{ ...navBtnStyle, opacity: canGoBack ? 1 : 0.3, cursor: canGoBack ? "pointer" : "default" }} onClick={() => { if (canGoBack) setOffset(o => o - 1); }} title={`Previous ${tipUnit}`}><ChevronLeft size={16} /></button>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)" }}>{label}</div>
                    {!isCurrent && (
                      <button onClick={() => setOffset(0)} style={{ background: "none", border: "none", fontSize: 10, color: "var(--c-text-accent)", cursor: "pointer", fontWeight: 600, padding: 0, marginTop: 1, fontFamily: "inherit" }}>↩ Back to current</button>
                    )}
                  </div>
                  <button style={{ ...navBtnStyle, opacity: isCurrent ? 1 : 1 }} onClick={() => setOffset(o => o + 1)} title={`Next ${tipUnit}`}><ChevronRight size={16} /></button>
                </div>
              );
            };

            const getCompletedInRange = (h, fromMs, toMs) => {
              const hStartStr = h.habitStartDate || localDateStr(h.createdAt) || todayStr();
              const hStartMs = new Date(hStartStr + "T00:00:00").getTime();
              const start = Math.max(fromMs, hStartMs);
              const isRestricted = h.habitType === "nottodo" || h.habitType === "limited";
              let count = 0;
              for (let ms = start; ms <= Math.min(toMs, todayMs); ms += 86400000) {
                const d = fmt(new Date(ms));
                if (isHabitExcludedOn(h, d)) continue; // a day off — the target leaves it out too
                if (isRestricted) {
                  if (h.allowanceMode) {
                    // Budget habit: each day auto-counts as a clean day unless
                    // you logged an attempt or skipped it that day.
                    const usedThatDay = (h.attemptLog || []).some(a => a === d);
                    const skippedThatDay = habitSkips.some(s => s.habitId === h.id && s.skippedDate === d);
                    if (!usedThatDay && !skippedThatDay) count++;
                    continue;
                  }
                  // Only restricted days count toward the boundary; allowed days are free
                  if (!isRestrictedDay(h, new Date(ms))) continue;
                  // Auto-complete: count as done unless slipped or skipped
                  if (isHabitDoneOn(h, habitLogs, d, habitSkips)) count++;
                } else if (isTodoCompleteOn(h, habitLogs, d)) {
                  count++;
                }
              }
              return count;
            };


            const HabitCell = ({ h }) => (
              <div onClick={() => setHabitCalendarModal({ habit: h, month: now().getMonth(), year: now().getFullYear() })} style={{ width: 120, flexShrink: 0, borderRight: "1px solid #f0dcea", padding: "6px 8px", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                <HabitIcon h={h} size={14} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: "var(--c-text-mid)", fontWeight: 600, lineHeight: 1.3, wordBreak: "break-word", whiteSpace: "normal" }}>{h.name}{goalTag(h) ? <span style={{ fontSize: 9, fontWeight: 700, color: vibrantAccent(h.color || "#d4789a"), marginLeft: 3 }}>({goalTag(h)})</span> : null}</span>
              </div>
            );
            const GoalHeader = ({ label, sectionLabel, sectionColor, note }) => (
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <div style={{ width: 120, flexShrink: 0, background: "var(--c-bg-card)", borderRight: "1px solid #f0dcea", padding: "4px 8px", display: "flex", alignItems: "center", minHeight: 36 }}>
                  <span style={{ fontSize: 10, color: sectionLabel ? (sectionColor || "var(--c-text-mid)") : "var(--c-text-mid)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{sectionLabel || "Habit"}</span>
                </div>
                {/* Explicit white/card surface (not inherited) so this label
                    keeps readable contrast regardless of the section's theme. */}
                <div style={{ flex: 1, background: "var(--c-bg-card)", padding: "4px 8px", display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 10, color: "var(--c-text-mid)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
                  {/* e.g. "Auto-completion" beside the main label on the Restricted section */}
                  {note && <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 700, color: sectionColor || "var(--c-text-mid)", background: "var(--c-bg-soft)", borderRadius: 99, padding: "2px 7px", letterSpacing: 0.3, whiteSpace: "nowrap" }}>{note}</span>}
                </div>
              </div>
            );
            const CheckboxRow = ({ h, idx, completedCount, target }) => {
              const displayTarget = Math.max(target, 1);
              const isBudget = !!h.allowanceMode && (h.habitType === "limited");
              // Budget habits: cap clean-day count so stacking uses on one day
              // never pushes the count past the target (no "6/5").
              const shownCount = isBudget ? Math.min(completedCount, displayTarget) : completedCount;
              const pct = Math.min(100, Math.round((shownCount / displayTarget) * 100));
              const cat = categories.find(c => c.id === h.goalId);
              const color = vividPastel(cat?.color || h.color);
              const isComplete = shownCount >= displayTarget;
              return (
                <div style={{ display: "flex", alignItems: "center", borderTop: "1px solid #f5eaf0", background: idx % 2 === 0 ? "var(--c-bg-soft)" : "var(--c-bg-card)", minHeight: 44 }}>
                  <HabitCell h={h} />
                  <div style={{ flex: 1, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 5 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: isComplete ? "#4caf50" : color }}>
                        {isComplete ? "✓ Complete!" : `${pct}%`}
                      </span>
                      <span style={{ fontSize: 10, color: "#d8c8d8", margin: "0 6px", letterSpacing: 0 }}>|</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-soft)" }}>{shownCount}<span style={{ fontWeight: 400, color: "var(--c-text-soft)" }}>/{displayTarget}</span><span style={{ fontSize: 9, fontWeight: 400, color: "var(--c-text-soft)", marginLeft: 2 }}>{isBudget ? "clean" : "done"}</span></span>
                    </div>
                    <div style={{ height: 8, borderRadius: 99, background: "var(--c-bg-soft)", overflow: "hidden" }}>
                      <div className="hs-fill" style={{ height: "100%", borderRadius: 99, background: isComplete ? "#4caf50" : color, width: `${pct}%`, transition: "width 0.4s ease" }} />
                    </div>
                  </div>
                </div>
              );
            };
            const ProgressRow = ({ h, idx, completedCount, target }) => {
              const displayTarget = Math.max(target, 1);
              const isBudget = !!h.allowanceMode && (h.habitType === "limited");
              const shownCount = isBudget ? Math.min(completedCount, displayTarget) : completedCount;
              const cat = categories.find(c => c.id === h.goalId);
              const color = vividPastel(cat?.color || h.color);
              return (
                <div style={{ display: "flex", alignItems: "center", borderTop: "1px solid #f5eaf0", background: idx % 2 === 0 ? "var(--c-bg-soft)" : "var(--c-bg-card)", minHeight: 36 }}>
                  <HabitCell h={h} />
                  <div style={{ flex: 1, padding: "6px 8px", display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: "var(--c-bg-soft)", overflow: "hidden" }}>
                      <div className="hs-fill" style={{ height: "100%", borderRadius: 4, background: color, width: `${Math.min(100, (shownCount / displayTarget) * 100)}%`, transition: "width 0.3s" }} />
                    </div>
                    <span style={{ fontSize: 12, color: "var(--c-text-soft)", fontWeight: 600, whiteSpace: "nowrap" }}>{shownCount}/{displayTarget}</span>
                  </div>
                </div>
              );
            };

            // ── Nav bar shared styles ──
            const navBtnStyle = {
              background: "none", border: "none", borderRadius: 10,
              width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "var(--c-text-mid)", fontSize: 14, fontWeight: 700,
              transition: "all 0.15s ease", flexShrink: 0,
            };

            if (!isPremium) {
              return (
                <CategorySection title={`${targetView === "weekly" ? "Weekly" : targetView === "monthly" ? "4-Week" : "Yearly"} Goal`} description={`Habit completion target (${targetView === "weekly" ? "this week" : targetView === "monthly" ? "this period" : "this year"})`} isEmpty={false}>
                  <PremiumLockedCard description="See how close each habit is to hitting its weekly, 4-week, or yearly goal." />
                </CategorySection>
              );
            }
            return (
              <>
                {targetView === "weekly" && (
                  <CategorySection title="Weekly Goal" description="Habit completion target (this week)" isEmpty={allGoalHabits.length === 0}>
                    <PeriodNavBar offset={tcWeekOffset} setOffset={setTcWeekOffset} periodDays={7} habitList={allGoalHabits} unitLabel="Week" />
                    {/* ── To-Do habits ── */}
                    {goalTodoHabits.length > 0 && (
                      <>
                        <GoalHeader label="Weekly Target" sectionLabel={goalRestrictedHabits.length > 0 ? "To-Do" : undefined} sectionColor="#6abf7a" />
                        {goalTodoHabits.map((h, idx) => {
                          const range = habitPeriodRange(h, 7, tcWeekOffset);
                          return <CheckboxRow key={h.id} h={h} idx={idx} completedCount={getCompletedInRange(h, range.startMs, range.endMs)} target={getWeeklyTarget(h, range)} />;
                        })}
                      </>
                    )}
                    {/* ── Restricted habits ── */}
                    {goalRestrictedHabits.length > 0 && (
                      <>
                        <GoalHeader label="Weekly Target" sectionLabel="Restricted" sectionColor="#e07090" note="Auto-completion" />
                        {goalRestrictedHabits.map((h, idx) => {
                          const range = habitPeriodRange(h, 7, tcWeekOffset);
                          return <CheckboxRow key={h.id} h={h} idx={idx} completedCount={getCompletedInRange(h, range.startMs, range.endMs)} target={getWeeklyTarget(h, range)} />;
                        })}
                      </>
                    )}
                  </CategorySection>
                )}
                {targetView === "monthly" && (
                  <CategorySection title="4-Week Goal" description="Habit completion target (this period)" isEmpty={allGoalHabits.length === 0}>
                    <PeriodNavBar offset={tcMonthOffset} setOffset={setTcMonthOffset} periodDays={HABIT_PERIOD_DAYS} habitList={allGoalHabits} unitLabel="4-Week" navUnit="period"
                      labels={{ current: "Current period", offset: (nOff, dir) => `${nOff} period${nOff > 1 ? "s" : ""} ${dir}` }} />
                    {/* ── To-Do habits ── */}
                    {goalTodoHabits.length > 0 && (
                      <>
                        <GoalHeader label="4-Week Target" sectionLabel={goalRestrictedHabits.length > 0 ? "To-Do" : undefined} sectionColor="#6abf7a" />
                        {goalTodoHabits.map((h, idx) => {
                          const range = habitPeriodRange(h, HABIT_PERIOD_DAYS, tcMonthOffset);
                          return <CheckboxRow key={h.id} h={h} idx={idx} completedCount={getCompletedInRange(h, range.startMs, range.endMs)} target={getMonthlyTarget(h, range)} />;
                        })}
                      </>
                    )}
                    {/* ── Restricted habits ── */}
                    {goalRestrictedHabits.length > 0 && (
                      <>
                        <GoalHeader label="4-Week Target" sectionLabel="Restricted" sectionColor="#e07090" note="Auto-completion" />
                        {goalRestrictedHabits.map((h, idx) => {
                          const range = habitPeriodRange(h, HABIT_PERIOD_DAYS, tcMonthOffset);
                          return <CheckboxRow key={h.id} h={h} idx={idx} completedCount={getCompletedInRange(h, range.startMs, range.endMs)} target={getMonthlyTarget(h, range)} />;
                        })}
                      </>
                    )}
                  </CategorySection>
                )}
                {targetView === "yearly" && (
                  <CategorySection title="Yearly Goal" description="Habit completion target (this year)" isEmpty={goalHabits.length === 0}>
                    <PeriodNavBar offset={tcYearOffset} setOffset={setTcYearOffset} periodDays={365} habitList={goalHabits} unitLabel="Year" />
                    {/* ── To-Do habits ── */}
                    {goalTodoHabits.length > 0 && (
                      <>
                        <GoalHeader label="Yearly Target" sectionLabel={goalRestrictedHabits.length > 0 ? "To-Do" : undefined} sectionColor="#6abf7a" />
                        {goalTodoHabits.map((h, idx) => {
                          const range = habitPeriodRange(h, 365, tcYearOffset);
                          return <ProgressRow key={h.id} h={h} idx={idx} completedCount={getCompletedInRange(h, range.startMs, range.endMs)} target={getYearlyTarget(h, range)} />;
                        })}
                      </>
                    )}
                    {/* ── Restricted habits ── */}
                    {goalRestrictedHabits.length > 0 && (
                      <>
                        <GoalHeader label="Yearly Target" sectionLabel="Restricted" sectionColor="#e07090" note="Auto-completion" />
                        {goalRestrictedHabits.map((h, idx) => {
                          const range = habitPeriodRange(h, 365, tcYearOffset);
                          return <ProgressRow key={h.id} h={h} idx={idx} completedCount={getCompletedInRange(h, range.startMs, range.endMs)} target={getYearlyTarget(h, range)} />;
                        })}
                      </>
                    )}
                  </CategorySection>
                )}
              </>
            );
          })()}
        </div>
      </Card>}

      {section === "habits" && isPremium && hasTickedHabit() && !targetGuideDone && (
        <ScrollCoachMark
          targetRef={completionTargetCardRef}
          title="Target Completion"
          body="This shows how close each habit is to hitting its goal for the week, 4-week period, or year."
          onDismiss={() => { markTargetCompletionGuideDone(); setTargetGuideDone(true); }}
        />
      )}
      {section === "habits" && isPremium && hasTickedHabit() && targetGuideDone && !overallGuideDone && (
        <ScrollCoachMark
          targetRef={barGraphRef}
          title="Overall Completion"
          body="Every bar represents your overall consistency across all habits within a 28 day period. Every time you tick a checkbox or follow a restricted habit, the bar gets taller, little by little. Try tapping a bar and see your current score percentage."
          onDismiss={() => { markOverallCompletionGuideDone(); setOverallGuideDone(true); }}
        />
      )}

      {/* ── OVERALL COMPLETION — its own section (whole-year M1..M12 view) ── */}
      {/* Independent of the Weekly/Monthly/Yearly tracker toggle above: each bar
          is one HABIT_PERIOD_DAYS window from the habit start date, so every bar
          starts on the same weekday and covers the same number of due days —
          which is what makes them comparable to each other. Matches the Habits
          History card width. */}
      {section === "habits" && !isPremium && (
        <Card className="hs-glass" title="📊 Overall Completion">
          <PremiumLockedCard description="Track your overall consistency across all habits over the whole year." />
        </Card>
      )}
      {section === "habits" && isPremium && (() => {
        const fmtRangeDate = (dateStr) => { const d = new Date(dateStr + "T00:00:00"); return `${MONTHS[d.getMonth()]} ${d.getDate()}`; };
        const isFirstYear = overallYearOffset <= 0;
        const goPrevYear = () => { if (isFirstYear) return; setOverallYearOffset(o => o - 1); setOverallBarSel(null); };
        const goNextYear = () => { setOverallYearOffset(o => o + 1); setOverallBarSel(null); };
        // Year navigation lives in the card's own header row (top-right, next
        // to "Overall Completion"), not inside the chart's inner container —
        // it pages between 12-month stretches measured from the start date.
        const yearNav = habits.length === 0 ? null : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={goPrevYear} disabled={isFirstYear} aria-label="Previous cycle"
              style={{ background: "none", border: "none", borderRadius: 10, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: isFirstYear ? "default" : "pointer", color: "var(--c-text-mid)", opacity: isFirstYear ? 0.3 : 1, flexShrink: 0 }}>
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-accent)", letterSpacing: 0.3, whiteSpace: "nowrap" }}>Cycle {overallYearOffset + 1}</span>
            <button onClick={goNextYear} aria-label="Next cycle"
              style={{ background: "none", border: "none", borderRadius: 10, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--c-text-mid)", flexShrink: 0 }}>
              <ChevronRight size={14} />
            </button>
          </div>
        );
        return (
      <Card className="hs-glass" title="📊 Overall Completion" headerRight={yearNav}>
        {habits.length === 0
          ? <p style={S.empty}>Add habits to see your overall completion</p>
          : (
            <div ref={barGraphRef} style={{ background: "var(--c-bg-card)", borderRadius: 12, padding: "10px 10px 6px", border: "1px solid #f0dcea" }}>
              <style>{`@keyframes clarityBadgeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>

              {/* Detail badge — fades in when a bar is tapped */}
              {overallBarSel != null && overallYearData[overallBarSel] ? (() => {
                const d = overallYearData[overallBarSel];
                const ordinal = (n) => { const v = n % 100; return `${n}${(v >= 11 && v <= 13) ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th")}`; };
                const periodLabel = `${ordinal(overallBarSel + 1)} period`;
                const rangeLabel = d.rangeStart && d.rangeEnd ? `${fmtRangeDate(d.rangeStart)} – ${fmtRangeDate(d.rangeEnd)}` : "Upcoming";
                return (
                  <div key={`${overallYearOffset}-${overallBarSel}`} style={{ animation: "clarityBadgeIn 0.25s ease", marginBottom: 10, padding: "4px 2px", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--c-text-accent)", lineHeight: 1, flexShrink: 0 }}>{d.value == null ? "-" : `${d.value}%`}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "var(--c-text-accent)", letterSpacing: 0.2 }}>{periodLabel}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--c-text-mid)", marginTop: 1 }}>{rangeLabel}</div>
                    </div>
                    <button onClick={() => setOverallBarSel(null)} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--c-text-soft)", padding: 4, flexShrink: 0 }}><X size={15} /></button>
                  </div>
                );
              })() : null}

              <BarChart data={overallYearData}
                // Default background (and dark mode): the user's own accent, not a fixed pink (Jade, 2026-10-21).
                color1={ACTIVE_BG_THEME === "none" ? "var(--c-primary-light)" : themeTone("#f4a7b9")}
                color2={ACTIVE_BG_THEME === "none" ? "var(--c-primary-deep)" : themeTone("#d4789a")} yLabel="%" maxY={100} axisFontSize={11}
                barGap={9} highlightIndex={overallBarSel}
                onBarClick={(i) => setOverallBarSel(prev => prev === i ? null : i)} />
            </div>
          )}
      </Card>
        );
      })()}

      {/* History Card — Full Implementation */}
      {section === "habits" && <div ref={historyCardRef}>
      <Card className="hs-glass" title="📋 Habits History">
        {/* Section C: Content */}
        {(() => {
          const dateObj = new Date(historyRecentsDate + "T00:00:00");
          const dayName = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dateObj.getDay()];
          const dateLabel = `${dayName}, ${MONTHS[dateObj.getMonth()]} ${dateObj.getDate()}`;
          const shiftDay = (dateStr, delta) => { const d = new Date(dateStr + "T00:00:00"); d.setDate(d.getDate() + delta); return fmt(d); };
          const isPastOrToday = historyRecentsDate <= todayStr();
          const isPast = historyRecentsDate < todayStr();
          // Budget/counted habits (weekly ×N, monthly-count, yearly-count) aren't
          // tied to specific days, so isHabitDueOn always excludes them here.
          // Surface them too, but only on a day they actually have a logged
          // count — they have no per-day due date, so there's no "missed" state
          // for a day with nothing logged.
          const isCountedHabit = (h) => h.frequencyType === "count"
            || (h.frequencyType === "monthly" && h.monthlyMode === "count")
            || (h.frequencyType === "yearly" && (h.yearlyMode || "count") === "count");
          const countHabitGoal = (h) => {
            const isYearlyCount = h.frequencyType === "yearly";
            const isMonthlyCount = h.frequencyType === "monthly";
            const periodLen = isYearlyCount ? 365 : isMonthlyCount ? 30 : 7;
            const target = isYearlyCount ? Math.max(1, parseInt(h.yearlyCount, 10) || 1) : isMonthlyCount ? (h.monthlyCount || 1) : (h.weeklyCount || 1);
            const originStr = h.habitStartDate || localDateStr(h.createdAt) || historyRecentsDate;
            const originMs = new Date(originStr + "T00:00:00").getTime();
            const dateMs = new Date(historyRecentsDate + "T00:00:00").getTime();
            const periodIdx = Math.max(0, Math.floor((dateMs - originMs) / (periodLen * 86400000)));
            const periodStartMs = originMs + periodIdx * periodLen * 86400000;
            let done = 0;
            for (let i = 0; i < periodLen; i++) {
              const key = fmt(new Date(periodStartMs + i * 86400000));
              if (key > historyRecentsDate) break;
              if (habitLogs[key]?.[h.id]) done++;
            }
            return { target, done };
          };
          const scheduledDueHabits = habits.filter(h => isHabitDueOn(h, historyRecentsDate) && historyRecentsDate >= (h.habitStartDate || localDateStr(h.createdAt) || ""));
          const countedDueHabits = habits.filter(h => isCountedHabit(h) && historyRecentsDate >= (h.habitStartDate || localDateStr(h.createdAt) || "") && habitLogs[historyRecentsDate]?.[h.id]);
          const dueHabits = [...scheduledDueHabits, ...countedDueHabits];
          const isDoneThatDay = (h) => (h.habitType === "nottodo" || h.habitType === "limited") ? isRestrictedDoneOn(h, historyRecentsDate) : isHabitDoneOn(h, habitLogs, historyRecentsDate, habitSkips);
          const doneHabits = dueHabits.filter(isDoneThatDay);
          const missedHabits = dueHabits.filter(h => !isDoneThatDay(h));
          const nothingRecorded = dueHabits.length === 0;

          const EditableRow = ({ id, label, icon, iconColor, rowBg, actionLabel, actionColor, actionBg, actionBorder, onAction, dateStr }) => {
            const key = `${dateStr}:${id}`;
            const isExpanded = expandedHistoryItem === key;
            const wasEdited = historyEditedItems[key];
            return (
              <div style={{ display: "flex", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #f5f0f8", gap: 6, background: isExpanded ? rowBg : "transparent", borderRadius: 6, transition: "background 0.15s" }}>
                <span style={{ color: iconColor, fontSize: 13, width: 16, flexShrink: 0 }}>{icon}</span>
                <span style={{ flex: 1, fontSize: 13, color: "var(--c-text)" }}>{label}</span>
                {wasEdited && <Pencil size={11} style={{ color: "var(--c-text-accent)", flexShrink: 0 }} />}
                {isPastOrToday && (
                  isExpanded ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                      <button onClick={onAction} {...tapOnly(() => { onAction(); })} style={{ fontSize: 10, fontWeight: 700, color: actionColor, background: actionBg, border: `1px solid ${actionBorder}`, borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontFamily: "inherit", touchAction: "manipulation" }}>{actionLabel}</button>
                      <button onClick={() => setExpandedHistoryItem(null)} style={{ fontSize: 11, color: "var(--c-text-soft)", background: "transparent", border: "none", cursor: "pointer", padding: "0 2px" }}><X size={16} /></button>
                    </div>
                  ) : (
                    <button onClick={() => setExpandedHistoryItem(key)} {...tapOnly(() => { setExpandedHistoryItem(key); })} style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text-soft)", background: "var(--c-bg-soft)", border: "1.5px solid #f0d0e8", borderRadius: 8, cursor: "pointer", padding: "2px 8px", lineHeight: 1.4, flexShrink: 0, touchAction: "manipulation" }}><ChevronRight size={16} /></button>
                  )
                )}
              </div>
            );
          };

          const isToday = historyRecentsDate === todayStr();
          const allDone = dueHabits.length > 0 && doneHabits.length === dueHabits.length;
          return (
            <div>
              {/* ── Date nav bar ── */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <button onClick={() => setHistoryRecentsDate(d => shiftDay(d, -1))} style={{ background: "none", border: "none", padding: "6px 12px", fontSize: 15, flexShrink: 0, cursor: "pointer", color: "var(--c-text-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={16} /></button>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text-accent)" }}>{dateLabel}</div>
                  {isToday && <div style={{ fontSize: 10, fontWeight: 700, color: "var(--c-text-soft)", letterSpacing: 0.8, marginTop: 1 }}>TODAY</div>}
                </div>
                <button onClick={() => { const next = shiftDay(historyRecentsDate, 1); if (next <= todayStr()) setHistoryRecentsDate(next); }} disabled={isToday} style={{ background: "none", border: "none", padding: "6px 12px", fontSize: 15, flexShrink: 0, cursor: "pointer", color: "var(--c-text-accent)", opacity: isToday ? 0.3 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronRight size={16} /></button>
                {!isToday && (
                  <button onClick={() => setHistoryRecentsDate(todayStr())} style={{ ...S.btn, fontSize: 11, padding: "5px 10px", color: "var(--c-text-accent)", borderColor: "var(--c-border)", fontWeight: 700, flexShrink: 0 }}><Calendar size={11} style={{marginRight:3}} />Today</button>
                )}
              </div>

              {/* ── Summary pill ── */}
              {dueHabits.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, padding: "10px 14px", background: allDone ? "var(--c-bg-active)" : "var(--c-bg-card)", borderRadius: 12, border: `1.5px solid ${allDone ? "#c8f0d0" : "#f0dcea"}` }}>
                  {!allDone && (
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: "var(--c-bg-card)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                      {doneHabits.length === 0 ? "📭" : "✨"}
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: allDone ? "#4caf50" : "var(--c-text-accent)" }}>
                      {allDone ? "All Completed ^^" : `${doneHabits.length} of ${dueHabits.length} done`}
                    </div>
                    {!allDone && dueHabits.length > 0 && (
                      <div style={{ marginTop: 5, height: 5, borderRadius: 99, background: "var(--c-bg-soft)", overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 99, background: "var(--c-primary)", width: `${Math.round((doneHabits.length / dueHabits.length) * 100)}%`, transition: "width 0.3s" }} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Habit list ── */}
              <div style={{ overflowY: "auto", maxHeight: 280 }}>
                {nothingRecorded ? (
                  <div style={{ textAlign: "center", padding: "32px 0" }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
                    <p style={{ margin: 0, fontSize: 13, color: "var(--c-text-soft)", fontStyle: "italic" }}>No habits scheduled for this day</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {dueHabits.map(h => {
                      const done = doneHabits.includes(h);
                      const key = `${historyRecentsDate}:${h.id}`;
                      const isExpanded = expandedHistoryItem === key;
                      const wasEdited = historyEditedItems[key];
                      const isRestricted = h.habitType === "nottodo" || h.habitType === "limited";
                      return (
                        <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 12, background: done ? "var(--c-bg-active)" : "var(--c-bg-card)", border: `1.5px solid ${done ? "#c8f0d0" : "#f5dcea"}`, transition: "background 0.15s" }}>
                          {/* Status dot */}
                          <div style={{ width: 26, height: 26, borderRadius: 8, background: done ? "#4caf50" : "#f0e8f0", border: `1.5px solid ${done ? "#4caf50" : "#e0d4e8"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {done ? <span style={{ fontSize: 13, color: "#fff", fontWeight: 700 }}>✓</span> : <span style={{ fontSize: 12, color: "var(--c-text-soft)" }}>✗</span>}
                          </div>
                          <HabitIcon h={h} size={14} style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: done ? "#2e7d40" : "#5a3050" }}>{h.name}{(() => { const gh = goalOn(h, historyRecentsDate); const tag = isAmountHabit(gh) ? formatProgress(gh, loggedAmount(gh, habitLogs[historyRecentsDate]?.[h.id])) : goalTag(gh); return tag ? <span style={{ fontSize: 10, fontWeight: 700, color: vibrantAccent(h.color || "#d4789a"), marginLeft: 4 }}>({tag})</span> : null; })()}</span>
                          {isRestricted && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: done ? "#3B6D11" : "#A32D2D", fontStyle: "italic", flexShrink: 0 }}>{done ? "clean" : h.allowanceMode ? "used" : "slipped"}</span>
                          )}
                          {isCountedHabit(h) && (() => { const g = countHabitGoal(h); return (
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#a07090", background: "var(--c-bg-soft)", border: "1px solid #f0dcea", borderRadius: 8, padding: "2px 7px", flexShrink: 0 }}>Goal: {g.done}/{g.target}</span>
                          ); })()}
                          {wasEdited && <Pencil size={12} style={{ color: "var(--c-text-accent)", flexShrink: 0 }} />}
                          {isPastOrToday && (
                            isExpanded ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                <button
                                  onClick={() => { toggleHabitLog(h.id, historyRecentsDate); setExpandedHistoryItem(null); }}
                                  {...tapOnly(() => { toggleHabitLog(h.id, historyRecentsDate); setExpandedHistoryItem(null); })}
                                  style={{ fontSize: 10, fontWeight: 700, color: done ? "#e8647a" : "#4caf50", background: done ? "#fff0f0" : "#f0fff4", border: `1px solid ${done ? "#ffd0d0" : "#c8f0d0"}`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", touchAction: "manipulation" }}
                                >{isRestricted ? (done ? (h.allowanceMode ? "Mark used" : "Mark slipped") : "Mark clean") : isAmountHabit(goalOn(h, historyRecentsDate)) ? "Edit amount" : (done ? "Mark missed" : "Mark done")}</button>
                                <button onClick={() => setExpandedHistoryItem(null)} {...tapOnly(() => { setExpandedHistoryItem(null); })} style={{ fontSize: 11, color: "var(--c-text-soft)", background: "transparent", border: "none", cursor: "pointer", padding: "0 2px", touchAction: "manipulation" }}><X size={14} /></button>
                              </div>
                            ) : (
                              <button onClick={() => setExpandedHistoryItem(key)} {...tapOnly(() => { setExpandedHistoryItem(key); })} style={{ fontSize: 12, color: "var(--c-text-soft)", background: "var(--c-bg-soft)", border: "1.5px solid #f0d0e8", borderRadius: 8, cursor: "pointer", padding: "2px 8px", flexShrink: 0, touchAction: "manipulation" }}><ChevronRight size={16} /></button>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Card>
      </div>}


      {/* Clock In Time */}
      {section === "focus" && (() => {
        // bare = the Clock-In Chart page: no card, black / white text and frosted
        // totals, like the timer page (Jade, 2026-10-24).
        const ci = bare ? clockInInk() : null;
        const Wrap = bare ? "div" : Card;
        return (
      <div ref={donutChartRef}><Wrap style={bare ? { padding: "0 12px" } : undefined}>
        {/* Desktop Chart page (wide): the totals are big frosted cards on top, then the
            donut beside the logged sessions (Jade's mockup G, 2026-11-02). */}
        {!(bare && wide) && <FocusPieChart focusSessions={focusSessions} setFocusSessions={setFocusSessions} habits={habits} habitLogs={habitLogs} setHabitLogs={setHabitLogs} tasks={tasks} setTasks={setTasks} categories={categories} setCategories={setCategories} isDesktop={isDesktop} toolbarSlotNode={statsToolbarSlotNode} viewSlotNode={statsViewSlotNode} weekStart={weekStart} bare={bare} />}
        <div style={bare && wide
          ? { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, marginTop: 18 }
          : { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          {[["This week", totalFocusWeek], ["This month", totalFocusMonth], ["All time", totalFocusAll]].map(([label, mins]) => (
            <div key={label} className={bare ? "ci-glass" : undefined} style={bare
              // The dark backgrounds' shadow on every background (Jade, 2026-11-02).
              ? { ...ci.glass, boxShadow: "0 4px 18px rgba(0,0,0,0.18)", borderRadius: wide ? 18 : 16, padding: wide ? "18px 22px" : "9px 14px", flex: 1 }
              : { background: "#f0fff4", borderRadius: 10, padding: "8px 14px", flex: 1 }}>
              <p style={{ margin: 0, fontSize: wide && bare ? 12 : 10, letterSpacing: wide && bare ? 1.2 : undefined, color: bare ? ci.soft : "#6a8070", fontWeight: 700 }}>{label.toUpperCase()}</p>
              <p style={{ margin: "2px 0 0", fontSize: wide && bare ? 32 : 16, fontWeight: wide && bare ? 900 : 700, fontFamily: wide && bare ? "'Nunito', sans-serif" : undefined, color: bare ? ci.ink : "#3a5040" }}>{Math.floor(mins/60)}h {String(mins%60).padStart(wide && bare ? 2 : 1, "0")}m</p>
            </div>
          ))}
        </div>
        {bare && wide && <div style={{ marginTop: 30 }}><FocusPieChart focusSessions={focusSessions} setFocusSessions={setFocusSessions} habits={habits} habitLogs={habitLogs} setHabitLogs={setHabitLogs} tasks={tasks} setTasks={setTasks} categories={categories} setCategories={setCategories} isDesktop={isDesktop} toolbarSlotNode={statsToolbarSlotNode} viewSlotNode={statsViewSlotNode} weekStart={weekStart} bare wide /></div>}
      </Wrap></div>
        );
      })()}
      {/* The "About Clarity" card was moved out of here — it now lives in
          AboutClarityCard (to be surfaced from the hamburger drawer later). */}
      {/* Habit Detail Modal */}
      {habitCalendarModal && (() => {
        const { habit: h, month, year } = habitCalendarModal;
        const modalCat = categories.find(c => c.id === h.goalId);
        const color = vividPastel(modalCat?.color || h.color);
        const daysInMo = new Date(year, month + 1, 0).getDate();
        const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
        const cells = [];
        for (let i = 0; i < firstDow; i++) cells.push(null);
        for (let d = 1; d <= daysInMo; d++) cells.push(d);
        const monthName = ["January","February","March","April","May","June","July","August","September","October","November","December"][month];
        const todayD = now().getDate();
        const isCurrentMonth = month === now().getMonth() && year === now().getFullYear();
        const DAY_LABELS_SHORT = ["Su","Mo","Tu","We","Th","Fr","Sa"];

        // ── Header progress line — this habit's OWN current period, anchored to
        // its own start date (not a shared calendar week/month). Counted habits
        // (weekly/monthly/yearly ×N) show their target progress + due date;
        // everyday/specific-day habits show this week's completion instead.
        // Restricted (nottodo/limited) habits don't have either concept, so no line.
        const isRestrictedHabit = h.habitType === "nottodo" || h.habitType === "limited";
        const isCountType = h.frequencyType === "count"
          || (h.frequencyType === "monthly" && h.monthlyMode === "count")
          || (h.frequencyType === "yearly" && (h.yearlyMode || "count") === "count");
        const modalHStartStr = h.habitStartDate || localDateStr(h.createdAt) || todayStr();
        const modalOriginMs = new Date(modalHStartStr + "T00:00:00").getTime();
        const modalTodayMs = new Date(todayStr() + "T00:00:00").getTime();
        const modalPeriodInfo = !isRestrictedHabit && (() => {
          const periodDays = isCountType ? (h.frequencyType === "yearly" ? 365 : h.frequencyType === "monthly" ? 30 : 7) : 7;
          let target = isCountType
            ? (h.frequencyType === "yearly" ? Math.max(1, parseInt(h.yearlyCount, 10) || 1) : h.frequencyType === "monthly" ? (h.monthlyCount || 1) : (h.weeklyCount || 1))
            : (h.frequencyType === "specific" ? ((h.days || []).length || 1) : 7);
          const currentIdx = Math.max(0, Math.floor((modalTodayMs - modalOriginMs) / (periodDays * 86400000)));
          const periodStartMs = modalOriginMs + currentIdx * periodDays * 86400000;
          const periodEndMs = periodStartMs + (periodDays - 1) * 86400000;
          // Interval habits: the due days that fall in this week, not 7.
          if (h.frequencyType === "interval") target = intervalDueDaysBetween(h, fmt(new Date(periodStartMs)), fmt(new Date(periodEndMs)));
          let done = 0;
          for (let ms = periodStartMs; ms <= Math.min(periodEndMs, modalTodayMs); ms += 86400000) {
            const d = fmt(new Date(ms));
            const v = habitLogs[d]?.[h.id];
            const t = goalOn(h, d).dailyCount || 1;
            if (typeof v === "number" ? v >= t : !!v) done++;
          }
          const dueDate = new Date(periodEndMs);
          const dueLabel = `${MONTHS[dueDate.getMonth()]} ${dueDate.getDate()}`;
          // The period's actual calendar span. "This week: 4/7" on its own
          // never says WHICH week — and it isn't the calendar week anyway:
          // every habit's periods are counted forward from its own start
          // date, so one habit's "week" can run Wed–Tue while another's runs
          // Sat–Fri. Spelling the dates out is the only way to know where the
          // count restarts. Years are shown only when the span isn't wholly
          // inside the current year (i.e. yearly habits, and periods that
          // straddle New Year).
          const startDate = new Date(periodStartMs);
          const spansOneYear = startDate.getFullYear() === dueDate.getFullYear();
          const withinThisYear = spansOneYear && startDate.getFullYear() === now().getFullYear();
          const dayLabel = (d) => `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}${withinThisYear ? "" : `, ${d.getFullYear()}`}`;
          const periodLabel = periodDays === 365 ? "year" : periodDays === 30 ? "month" : "week";
          const rangeLabel = `${dayLabel(startDate)} – ${dayLabel(dueDate)}`;
          return { done, target, dueLabel, rangeLabel, periodLabel };
        })();
        // ── Limited habits on an allowance budget ────────────────────────
        // Same idea as modalPeriodInfo above, for a budget being spent rather
        // than a target being met, and anchored to the habit's own start date
        // the same way (see allowancePeriodBounds). The count and the dates
        // both come from that one helper, so this label can't disagree with
        // the tracker card that opened the panel.
        const modalLimitInfo = !!h.allowanceMode && (() => {
          const max = h.allowanceMax || 1;
          const isMonth = h.allowanceMode === "month" || h.allowanceMode === "monthDates";
          const todayF = todayStr();
          const { start, end, reset } = allowancePeriodBounds(h, todayF);
          const startD = new Date(start + "T00:00:00");
          const endD = new Date(end + "T00:00:00");
          const used = allowanceUsed(h, todayF);
          const sameYear = startD.getFullYear() === endD.getFullYear() && startD.getFullYear() === now().getFullYear();
          const dayLabel = (ds) => { const d = new Date(ds + "T00:00:00"); return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}${sameYear ? "" : `, ${d.getFullYear()}`}`; };
          return {
            used, max, over: used > max,
            periodLabel: isMonth ? "Month" : "Week",
            rangeLabel: `${dayLabel(start)} – ${dayLabel(end)}`,
            resetLabel: dayLabel(reset),
          };
        })();
        return (
          <Modal onClose={() => setHabitCalendarModal(null)} blur={false} dim={0.6}>
            <div onClick={e => e.stopPropagation()} style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "22px 18px 26px 18px", width: "100%", maxWidth: 360, boxShadow: "0 8px 40px rgba(180,100,140,0.22)" }}>
              {/* Header: icon, name, X. The month arrows sit on their own row below,
                  so a long name gets the full width (Jade, 2026-10-20: a long name
                  pushed the arrows and X out of the panel). */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: `${color}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <HabitIcon h={h} size={22} />
                </div>
                {/* minWidth 0: one unbreakable word ("doomscrolling/watching") can't
                    widen the header. A zero-width space after each "/" lets it wrap there. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text)", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" }}>{h.name.replace(/\//g, "/\u200B")}{goalTag(h) ? <span style={{ fontSize: 11, fontWeight: 600, color: "var(--c-text-accent)", marginLeft: 5 }}>({goalTag(h)})</span> : null}</div>
                  {modalPeriodInfo && (<>
                    <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 3 }}>
                      {isCountType
                        ? `Target: ${modalPeriodInfo.done}/${modalPeriodInfo.target} | Due: ${modalPeriodInfo.dueLabel}`
                        : `This ${modalPeriodInfo.periodLabel}: ${modalPeriodInfo.done}/${modalPeriodInfo.target}`}
                    </div>
                    {/* Which span that count actually covers — see rangeLabel.
                        Capitalised bare period word rather than "This week"
                        again, which the line above already says. */}
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--c-text-soft)", marginTop: 1 }}>
                      {modalPeriodInfo.periodLabel === "year" ? "Year" : modalPeriodInfo.periodLabel === "month" ? "Month" : "Week"}: {modalPeriodInfo.rangeLabel}
                    </div>
                  </>)}
                  {/* Limited/allowance habits — same two-line shape, but the
                      count is a budget being spent, not a target being met. */}
                  {modalLimitInfo && (<>
                    <div style={{ fontSize: 11, fontWeight: 700, color: modalLimitInfo.over ? "#A32D2D" : color, marginTop: 3 }}>
                      Used: {modalLimitInfo.used}/{modalLimitInfo.max}{modalLimitInfo.over ? ` (over by ${modalLimitInfo.used - modalLimitInfo.max})` : ""} | Resets: {modalLimitInfo.resetLabel}
                    </div>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--c-text-soft)", marginTop: 1 }}>
                      {modalLimitInfo.periodLabel}: {modalLimitInfo.rangeLabel}
                    </div>
                  </>)}
                </div>
                <button onClick={() => setHabitCalendarModal(null)} style={{ border: "none", background: "var(--c-bg-soft)", borderRadius: 8, width: 28, height: 28, cursor: "pointer", fontSize: 15, color: "#a080a0", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><X size={16} /></button>
              </div>
              {/* Month: ‹ October 2026 › */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
                <button onClick={() => {
                  const newMonth = month === 0 ? 11 : month - 1;
                  const newYear = month === 0 ? year - 1 : year;
                  setHabitCalendarModal({ habit: h, month: newMonth, year: newYear });
                }} style={{ border: "none", background: `${color}15`, borderRadius: 8, width: 28, height: 28, cursor: "pointer", fontSize: 14, color: color, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ChevronLeft size={16} /></button>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--c-text)" }}>{monthName} {year}</div>
                <button onClick={() => {
                  const newMonth = month === 11 ? 0 : month + 1;
                  const newYear = month === 11 ? year + 1 : year;
                  setHabitCalendarModal({ habit: h, month: newMonth, year: newYear });
                }} style={{ border: "none", background: `${color}15`, borderRadius: 8, width: 28, height: 28, cursor: "pointer", fontSize: 14, color: color, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ChevronRight size={16} /></button>
              </div>
              {/* Day labels */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 6 }}>
                {DAY_LABELS_SHORT.map(d => (
                  <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "var(--c-text-soft)", paddingBottom: 4 }}>{d}</div>
                ))}
              </div>
              {/* Calendar grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                {cells.map((day, i) => {
                  if (!day) return <div key={i} />;
                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const checked = !!habitLogs[dateStr]?.[h.id] && (typeof habitLogs[dateStr]?.[h.id] === "number" ? habitLogs[dateStr]?.[h.id] >= (goalOn(h, dateStr).dailyCount || 1) : true);
                  const isToday = isCurrentMonth && day === todayD;
                  const isFuture = dateStr > todayStr();
                  return (
                    <div key={i} style={{
                      aspectRatio: "1",
                      borderRadius: 8,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: isToday ? 800 : 500,
                      background: checked ? color : isToday ? `${color}22` : "transparent",
                      color: checked ? "#fff" : isToday ? color : isFuture ? "#d0c8dc" : "#5a3050",
                      border: isToday && !checked ? `2px solid ${color}` : "2px solid transparent",
                      boxSizing: "border-box",
                      opacity: isFuture ? 0.4 : 1,
                      cursor: "default",
                    }}>
                      {day}
                    </div>
                  );
                })}
              </div>
              {/* Legend */}
              <div style={{ display: "flex", gap: 16, marginTop: 16, justifyContent: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
                  <span style={{ fontSize: 11, color: "#a090b0" }}>Completed</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, border: `2px solid ${color}`, background: `${color}22` }} />
                  <span style={{ fontSize: 11, color: "#a090b0" }}>Today</span>
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}

      {habitDetailModal && (() => {
        const h = habitDetailModal;
        const cat = categories.find(c => c.id === h.goalId);
        const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
        const freqLabel = h.frequencyType === "daily" ? "Every day"
          : h.frequencyType === "interval" ? intervalLabel(h)
          : h.frequencyType === "count" ? `${h.weeklyCount || 1}× per week`
          : h.frequencyType === "specific" ? ((h.days || []).map(d => DAYS[d]).join(", ") || "Specific days")
          : h.frequencyType === "monthly" ? (h.monthlyMode === "count" ? `${h.monthlyCount || 10}× per month` : `Monthly (${(h.monthDates||[]).join(", ")})`)
          : h.frequencyType;
        // Consistency score
        const { score, cleanDays, totalDays } = getConsistencyScore(h, habitLogs, habitSkips);
        const scoreColor = score >= 80 ? "#3B6D11" : score >= 50 ? "#c27d20" : "#d4789a";
        const scoreBg    = score >= 80 ? "#f0faf5" : score >= 50 ? "#fef3e2" : "#fff0f5";
        const scoreMsg   = score >= 80 ? "doing great 🌿" : score >= 50 ? "keep going 💛" : "you've got this 🌸";

        // Slip history for nottodo + limited
        const slips = (h.relapseLog || [])
          .slice()
          .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

        // Dot grid — spans exactly the period the consistency score above it is
        // measured over, so the dots and the number can't describe different windows.
        const dotDays = Array.from({ length: HABIT_PERIOD_DAYS }, (_, i) => {
          const d = new Date(); d.setDate(d.getDate() - (29 - i));
          const key = fmt(d);
          let state = "empty"; // empty | clean | slip
          if (key <= t) {
            if (h.habitType === "nottodo") {
              state = h.relapseLog?.some(r => r.date === key) ? "slip" : "clean";
            } else if (h.habitType === "limited") {
              const logVal = habitLogs[key]?.[h.id];
              const inst = typeof logVal === "number" ? logVal : (logVal ? 1 : 0);
              const max = h.maxTimesPerDay || 1;
              state = inst > max ? "slip" : inst > 0 ? "clean" : "empty";
            } else {
              const logVal = habitLogs[key]?.[h.id];
              const target = goalOn(h, key).dailyCount || 1;
              const due = isHabitDueOn(h, key);
              if (!due) { state = "na"; }
              else { state = (typeof logVal === "number" ? logVal >= target : !!logVal) ? "clean" : "empty"; }
            }
          }
          return { key, state };
        });

        const dotColor = { clean: "#4CAF50", slip: "#f5a623", empty: "#f0e8f0", na: "#f8f4f8" };

        return (
          <Modal onClose={() => setHabitDetailModal(null)} blur dim={0.45}>
            <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "24px 22px", width: "100%", maxWidth: 480, boxShadow: "0 8px 40px rgba(180,100,140,0.22)", maxHeight: "85vh", overflowY: "auto" }}>

              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <HabitIcon h={h} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#5a2040", lineHeight: 1.3 }}>{h.name}{goalTag(h) ? <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-text-accent)", marginLeft: 6 }}>({goalTag(h)})</span> : null}</div>
                  {cat && <div style={{ fontSize: 12, color: "var(--c-text-accent)", marginTop: 3 }}><CategoryIcon cat={cat} size={12} /> {cat.title}</div>}
                  <div style={{ fontSize: 11, color: "var(--c-text-soft)", marginTop: 2, textTransform: "capitalize" }}>{h.habitType || "todo"} habit · {freqLabel}</div>
                </div>
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: h.color || "#d4789a", flexShrink: 0 }} />
              </div>

              {/* Consistency Score */}
              {totalDays > 0 && (
                <div style={{ background: scoreBg, borderRadius: 14, padding: "14px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 14 }}>
                  <div>
                    <span style={{ fontSize: 32, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{score}%</span>
                    <div style={{ fontSize: 10, color: scoreColor, opacity: 0.8, marginTop: 2 }}>{scoreMsg}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    {(h.habitType === "nottodo" || h.habitType === "limited") ? (<>
                      <div style={{ fontSize: 12, color: "var(--c-text-mid)", fontWeight: 600 }}>🚫 Restriction Streak</div>
                      {slips.length === 0
                        ? <div style={{ fontSize: 11, color: "#3B6D11", marginTop: 2 }}>Perfect so far! 🌟 0 of {totalDays} days slipped</div>
                        : <div style={{ fontSize: 11, color: "#c0392b", marginTop: 2 }}>{slips.length} of {totalDays} day{totalDays !== 1 ? "s" : ""} slipped</div>
                      }
                    </>) : (<>
                      <div style={{ fontSize: 12, color: "var(--c-text-mid)", fontWeight: 600 }}>🌿 Completion Rate</div>
                      {(h.habitType === "nottodo" || h.habitType === "limited")
                      ? ((() => { const ps = getPeriodBounds(h, todayStr()).periodStart; const slipCount = (h.relapseLog || []).filter(r => r.date >= ps).length; return slipCount === 0
                          ? <div style={{ fontSize: 11, color: "#3B6D11", marginTop: 2 }}>Perfect so far! 🌟 0 of {totalDays} days slipped</div>
                          : <div style={{ fontSize: 11, color: "#c0392b", marginTop: 2 }}>{slipCount} of {totalDays} day{totalDays !== 1 ? "s" : ""} slipped</div>; })())
                      : <div style={{ fontSize: 11, color: "var(--c-text-soft)", marginTop: 2 }}>{cleanDays}/{totalDays} days completed this period</div>
                    }
                      {slips.length > 0 && (
                        <div style={{ fontSize: 11, color: "#f5a623", marginTop: 2 }}>😬 {slips.length} slip{slips.length > 1 ? "s" : ""} total</div>
                      )}
                    </>)}
                  </div>
                </div>
              )}

              {/* Period dot grid */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>Last 30 Days</div>
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {dotDays.map(({ key, state }) => (
                    <div key={key} title={key} style={{
                      width: 14, height: 14, borderRadius: 3,
                      background: dotColor[state] || "#f0e8f0",
                      border: key === t ? "2px solid #3a3040" : "1.5px solid transparent",
                      opacity: state === "na" ? 0.2 : state === "empty" ? 0.4 : 1,
                      flexShrink: 0,
                    }} />
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                  {[["#4CAF50","clean"],["#f5a623","slip"],["#f0e8f0","not logged"]].map(([c,l]) => (
                    <div key={l} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
                      <span style={{ fontSize: 9, color: "var(--c-text-soft)" }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Breakdown */}
              {(() => {
                const breakdown = getMonthlyBreakdown(h, habitLogs, 6, habitSkips);
                const hasData = breakdown.some(m => m.pastDueDays > 0);
                if (!hasData) return null;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 8 }}>📅 Period Completion Rate</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {breakdown.map(({ label, score, completedDays, pastDueDays, totalDueDays, periodStart }) => {
                        const todayPeriodStart = getPeriodStart(h, todayStr());
                        const isCurrentPeriod = periodStart === todayPeriodStart;
                        const color = score >= 80 ? "#3B6D11" : score >= 50 ? "#c27d20" : "#d4789a";
                        const bg = score >= 80 ? "#f0faf5" : score >= 50 ? "#fef3e2" : "#fff0f5";
                        const barColor = score >= 80 ? "#4CAF50" : score >= 50 ? "#f5a623" : "#d4789a";
                        if (pastDueDays === 0) return null;
                        return (
                          <div key={label} style={{ background: isCurrentPeriod ? bg : "#fdf8fc", borderRadius: 10, padding: "8px 12px", border: isCurrentPeriod ? `1.5px solid ${barColor}33` : "1px solid #f0dcea" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                              <span style={{ fontSize: 11, fontWeight: isCurrentPeriod ? 700 : 500, color: isCurrentPeriod ? "#5a2040" : "#a07090" }}>
                                {label} {isCurrentPeriod && <span style={{ fontSize: 9, color: barColor, fontWeight: 700 }}>● current</span>}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 800, color }}>{score}%</span>
                            </div>
                            <div style={{ height: 5, background: "var(--c-bg-active)", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${score}%`, background: barColor, borderRadius: 3, transition: "width 0.4s ease" }} />
                            </div>
                            <div style={{ fontSize: 9, color: "var(--c-text-soft)", marginTop: 4 }}>
                              {completedDays}/{pastDueDays} days completed
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Slip Reflections — only for nottodo + limited */}
              {(h.habitType === "nottodo" || h.habitType === "limited") && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: slips.length > 0 ? 10 : 6 }}>
                    😬 Slip Reflections
                  </div>
                  {slips.length === 0 ? (
                    <div style={{ background: "var(--c-bg-soft)", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#3B6D11" }}>
                      No slips recorded yet. Keep it up! 🌿
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto" }}>
                      {slips.map((slip, i) => (
                        <div key={i} style={{ background: "var(--c-bg-card)", border: "1px solid #f0e0ea", borderRadius: 10, padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: slip.note ? 6 : 0 }}>
                            <span style={{ fontSize: 14 }}>😬</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#f5a623" }}>{slip.date}</span>
                            {slip.type === "limited" && slip.count > 0 && (
                              <span style={{ fontSize: 10, color: "var(--c-text-soft)", background: "var(--c-bg-soft)", borderRadius: 6, padding: "1px 6px" }}>
                                {slip.count}× (limit: {h.maxTimesPerDay || 1})
                              </span>
                            )}
                          </div>
                          {slip.note ? (
                            <p style={{ margin: 0, fontSize: 12, color: "var(--c-text-mid)", fontStyle: "italic", borderLeft: "3px solid #f5a623", paddingLeft: 8, lineHeight: 1.5 }}>
                              "{slip.note}"
                            </p>
                          ) : (
                            <p style={{ margin: 0, fontSize: 11, color: "var(--c-text-soft)", fontStyle: "italic" }}>No note added</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button onClick={() => setHabitDetailModal(null)} style={{ width: "100%", padding: "12px", borderRadius: 14, background: "var(--c-bg-card)", border: "none", color: "var(--c-text-accent)", fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 4, fontFamily: "inherit" }}><X size={14} style={{marginRight:4}} />Close</button>
            </div>
          </Modal>
        );
      })()}
      {/* ── Floating back-to-top button */}
      <button
        onClick={() => { const el = document.querySelector(".clarity-main"); el ? el.scrollTo({ top: 0, behavior: "smooth" }) : window.scrollTo({ top: 0, behavior: "smooth" }); }}
        title="Back to top"
        style={{
          position: "fixed", bottom: "calc(80px + var(--sab, 0px))", right: 20, zIndex: 999,
          background: "transparent", border: "none",
          cursor: "pointer", padding: 4,
          color: "var(--c-text-accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          opacity: showBackToTop ? 1 : 0,
          pointerEvents: showBackToTop ? "auto" : "none",
          transform: showBackToTop ? "translateY(0)" : "translateY(10px)",
          transition: "opacity 0.25s, transform 0.25s",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = showBackToTop ? "translateY(0)" : "translateY(10px)"; }}
      >
        <ChevronUp size={28} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// Memoized: ProfileTab gets re-mounted as a desktop sidebar inside HabitsTab
// (and used standalone as its own tab), so without this every unrelated
// parent state change (e.g. any click elsewhere in the app) forced a full
// re-render and re-run of its hooks on the main thread.
export const ProfileTab = React.memo(ProfileTabInner);
